import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Check, EyeOff, ChevronRight, ChevronLeft, Building2, User, Lock, ShieldCheck, 
  TrendingUp, TrendingDown, PanelLeftClose, PanelRightClose, X, Shield, Loader2, Merge, UserPlus, FolderEdit,
  ArrowUpDown, ArrowUp, ArrowDown, Trash2, Package, ExternalLink, Plus, CheckCircle2, Eye, Download, DatabaseZap
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { customerApi, lookupApi, approvalApi, statsApi, masterApi, exportApi, adminApi } from '../services/api';
import { formatSmartCurrency } from '../utils/formatCurrency';
import { toast } from 'sonner';
import { getDivisionColor, getAccountManagerStats, calculateDiff } from './customer/utils';
import { DivisionPanel } from './customer/DivisionPanel';
import { FilterPanel } from './customer/FilterPanel';

export const CustomerList = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, error: authError, isAdmin } = useAuth();
  
  // Data state
  const [customers, setCustomers] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [divisionStats, setDivisionStats] = useState([]);  // Stats for sidebar
  const [accountManagers, setAccountManagers] = useState([]);
  const [loading, setLoading] = useState(false);  // Only for initial auth gate
  const [loadingDivisions, setLoadingDivisions] = useState(true);  // Division panel loading
  const [loadingCustomers, setLoadingCustomers] = useState(true);  // Customer data loading
  const [error, setError] = useState(null);
  
  // Admin lazy loading state
  const [managerStats, setManagerStats] = useState([]);  // Manager cards with stats (no customers)
  const [expandedManager, setExpandedManager] = useState(null);  // Currently expanded manager
  const [loadingManager, setLoadingManager] = useState(null);  // Manager whose customers are loading
  const [loadingDivision, setLoadingDivision] = useState(false);  // Loading state for division switching
  
  // Filter state - restore from sessionStorage
  const [searchQuery, setSearchQuery] = useState(() => sessionStorage.getItem('cl_searchQuery') || '');
  const [statusFilter, setStatusFilter] = useState(() => sessionStorage.getItem('cl_statusFilter') || 'all');
  const [divisionFilter, setDivisionFilter] = useState(() => sessionStorage.getItem('cl_divisionFilter') || 'all');
  const [accountManagerFilter, setAccountManagerFilter] = useState(() => sessionStorage.getItem('cl_accountManagerFilter') || 'all');
  const [approvalFilter, setApprovalFilter] = useState(() => sessionStorage.getItem('cl_approvalFilter') || 'all');
  const [hideCompleted, setHideCompleted] = useState(() => sessionStorage.getItem('cl_hideCompleted') === 'true');
  const [hideApproved, setHideApproved] = useState(() => sessionStorage.getItem('cl_hideApproved') === 'true');
  const [pipelineFilter, setPipelineFilter] = useState(() => sessionStorage.getItem('cl_pipelineFilter') || 'all');
  const [stageFilter, setStageFilter] = useState(() => sessionStorage.getItem('cl_stageFilter') || 'all');
  const [showNewDealSuspects, setShowNewDealSuspects] = useState(() => sessionStorage.getItem('cl_showNewDealSuspects') === 'true');
  const [showNoLineCards, setShowNoLineCards] = useState(() => sessionStorage.getItem('cl_showNoLineCards') === 'true');
  
  // Sorting state
  const [sortField, setSortField] = useState(() => sessionStorage.getItem('cl_sortField') || 'name');
  const [sortDirection, setSortDirection] = useState(() => sessionStorage.getItem('cl_sortDirection') || 'asc');
  
  // User-specific budget stats (non-admin)
  const [userStats, setUserStats] = useState(null);
  
  // Panel visibility state
  const [leftPanelOpen, setLeftPanelOpen] = useState(true);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);
  
  // Selected division for center panel
  const [selectedDivision, setSelectedDivision] = useState(null);
  
  // Confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(null);
  
  // Row selection state
  const [selectedRows, setSelectedRows] = useState(new Set());
  
  // Bulk action dialog states
  const [showMergeDialog, setShowMergeDialog] = useState(false);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [showDivisionDialog, setShowDivisionDialog] = useState(false);
  const [mergeTargetCustomer, setMergeTargetCustomer] = useState('');
  const [transferManager, setTransferManager] = useState('');
  const [newDivision, setNewDivision] = useState('');
  const [allAccountManagers, setAllAccountManagers] = useState([]);
  const [mergeTargets, setMergeTargets] = useState([]);  // All valid customers for merge dropdown
  const [loadingMergeTargets, setLoadingMergeTargets] = useState(false);
  const [mergeSearchQuery, setMergeSearchQuery] = useState('');  // Search query for merge targets
  const [mergeMode, setMergeMode] = useState('existing');  // 'existing' or 'mother'
  const [motherName, setMotherName] = useState('');

  // Pipeline drilldown dialog state
  const [drilldownCustomer, setDrilldownCustomer] = useState(null);
  const [drilldownPipelines, setDrilldownPipelines] = useState([]);
  const [drilldownLoading, setDrilldownLoading] = useState(false);
  const [drilldownCanDeleteAll, setDrilldownCanDeleteAll] = useState(false);
  const [deletingPipeline, setDeletingPipeline] = useState(null);  // pipeline being deleted

  // Add Deal dialog state
  const [showAddDealDialog, setShowAddDealDialog] = useState(false);
  const [addDealDivision, setAddDealDivision] = useState('');
  const [addDealEmpId, setAddDealEmpId] = useState('');
  const [addDealEmpName, setAddDealEmpName] = useState('');
  const [addDealName, setAddDealName] = useState('');
  const [addDealIsNoCustomer, setAddDealIsNoCustomer] = useState(false);
  const [addDealLoading, setAddDealLoading] = useState(false);

  // Reviewed managers state (admin only)
  const [reviewedManagers, setReviewedManagers] = useState({}); // { 'DIV|EMP': {reviewed_date, is_today} }
  const [hideReviewed, setHideReviewed] = useState(() => sessionStorage.getItem('cl_hideReviewed') !== 'false'); // default ON
  const [approvingManager, setApprovingManager] = useState(null); // 'DIV|EMP' being approved
  const [reviewingManager, setReviewingManager] = useState(null); // 'DIV|EMP' being reviewed
  const [exportingManager, setExportingManager] = useState(null); // 'DIV|EMP' being exported
  const [syncingToMySQL, setSyncingToMySQL] = useState(false);
  const [syncDivision, setSyncDivision] = useState('__all__');   // '__all__' = sync every division

  // Pipeline KPIs state (fetched from backend per expanded manager)
  const [managerPipelineKpis, setManagerPipelineKpis] = useState(null); // { pipelines: {...}, ly_budget_total, target_map }
  const [pipelineKpisLoading, setPipelineKpisLoading] = useState(false);
  const [editingTargets, setEditingTargets] = useState({}); // { pipeline: value }
  const [savingTarget, setSavingTarget] = useState(null); // pipeline being saved

  // Add Manager dialog state (admin only)
  const [showAddManagerDialog, setShowAddManagerDialog] = useState(false);
  const [addManagerSelectedUser, setAddManagerSelectedUser] = useState(''); // emp_id from dropdown
  const [allUsers, setAllUsers] = useState([]); // [{emp_id, name}]
  const [addManagerLoading, setAddManagerLoading] = useState(false);

  // Add Division dialog state (user)
  const [showAddDivisionDialog, setShowAddDivisionDialog] = useState(false);
  const [addDivisionSelected, setAddDivisionSelected] = useState(''); // selected from dropdown
  const [allDivisions, setAllDivisions] = useState([]); // string[]
  const [addDivisionLoading, setAddDivisionLoading] = useState(false);
  
  // Ref for scrolling back to customer after returning from budget page
  const customerTableRef = useRef(null);
  // Fetch merge targets when dialog opens
  useEffect(() => {
    if (showMergeDialog && mergeTargets.length === 0 && !loadingMergeTargets) {
      setLoadingMergeTargets(true);
      customerApi.getMergeTargets()
        .then(targets => {
          console.log('Loaded merge targets:', targets?.length);
          setMergeTargets(targets || []);
        })
        .catch(err => {
          console.error('Failed to load merge targets:', err);
          toast.error('Failed to load customer list');
        })
        .finally(() => setLoadingMergeTargets(false));
    }
  }, [showMergeDialog, mergeTargets.length, loadingMergeTargets]);

  // Persist filters to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('cl_searchQuery', searchQuery);
    sessionStorage.setItem('cl_statusFilter', statusFilter);
    sessionStorage.setItem('cl_divisionFilter', divisionFilter);
    sessionStorage.setItem('cl_accountManagerFilter', accountManagerFilter);
    sessionStorage.setItem('cl_approvalFilter', approvalFilter);
    sessionStorage.setItem('cl_hideCompleted', hideCompleted);
    sessionStorage.setItem('cl_hideApproved', hideApproved);
    sessionStorage.setItem('cl_pipelineFilter', pipelineFilter);
    sessionStorage.setItem('cl_stageFilter', stageFilter);
    sessionStorage.setItem('cl_showNewDealSuspects', showNewDealSuspects);
    sessionStorage.setItem('cl_showNoLineCards', showNoLineCards);
    sessionStorage.setItem('cl_sortField', sortField);
    sessionStorage.setItem('cl_sortDirection', sortDirection);
  }, [searchQuery, statusFilter, divisionFilter, accountManagerFilter, approvalFilter, hideCompleted, hideApproved, pipelineFilter, stageFilter, showNewDealSuspects, sortField, sortDirection]);

  // Fetch data on mount - PROGRESSIVE loading
  // Division stats & customer data load independently
  useEffect(() => {
    if (authLoading || !user) {
      if (!authLoading && !user) setLoadingCustomers(false);
      return;
    }
    
    // Load division stats in background (sidebar)
    const fetchDivisionStats = async () => {
      try {
        setLoadingDivisions(true);
        const [divisionsData, divisionStatsData, allManagersData] = await Promise.all([
          lookupApi.getDivisions(),
          lookupApi.getDivisionStats(),
          lookupApi.getAccountManagers()
        ]);
        setDivisions(divisionsData);
        setDivisionStats(divisionStatsData);
        setAllAccountManagers(allManagersData);
        // Pre-populate dialog data so dialogs open instantly
        setAllUsers(allManagersData);
        setAllDivisions(divisionsData);
        
        // Auto-select initial division
        if (!selectedDivision && divisionStatsData.length > 0) {
          if (!isAdmin) {
            const sortedByCount = [...divisionStatsData].sort((a, b) => 
              (b.customer_count || 0) - (a.customer_count || 0)
            );
            setSelectedDivision(sortedByCount[0].division);
          } else {
            // Admin: pick division with most customers for better UX
            const sortedByCount = [...divisionStatsData].sort((a, b) => 
              (b.customer_count || 0) - (a.customer_count || 0)
            );
            setSelectedDivision(sortedByCount[0].division);
          }
        } else if (!selectedDivision && divisionsData.length > 0) {
          setSelectedDivision(divisionsData[0]);
        }
      } catch (err) {
        console.error('Failed to fetch division stats:', err);
      } finally {
        setLoadingDivisions(false);
      }
    };
    
    // Load customer/manager data independently
    const fetchCustomerData = async () => {
      try {
        setLoadingCustomers(true);
        if (!isAdmin) {
          const customersData = await customerApi.getAll();
          setCustomers(customersData);
          // User stats will be fetched when division is selected (via useEffect)
        }
        // For admin: customers load lazily on division select (handled by separate useEffect)
      } catch (err) {
        console.error('Failed to fetch customers:', err);
        setError(err.message);
        toast.error('Failed to load customers');
      } finally {
        setLoadingCustomers(false);
      }
    };
    
    // Fire both in parallel - neither blocks the other
    fetchDivisionStats();
    fetchCustomerData();
  }, [user, authLoading, isAdmin]);
  
  // Fetch manager stats when admin changes division
  useEffect(() => {
    // Track the current division to avoid race conditions
    let isCurrent = true;
    
    const fetchManagerStats = async () => {
      if (!isAdmin || !selectedDivision || authLoading || !user) return;
      
      // IMMEDIATELY clear stale data before fetching new data
      // This prevents cards from "sticking" when switching divisions
      setLoadingDivision(true);
      setManagerStats([]);
      setCustomers([]);
      setExpandedManager(null);
      setAccountManagers([]);
      
      try {
        const managerStatsData = await lookupApi.getManagerStats(selectedDivision);
        
        // Only update state if this is still the current division
        if (isCurrent) {
          setManagerStats(managerStatsData);
          setAccountManagers(managerStatsData.map(m => m.name));
        }
      } catch (err) {
        console.error('Failed to fetch manager stats:', err);
        if (isCurrent) {
          // Keep empty state on error
          setManagerStats([]);
        }
      } finally {
        if (isCurrent) {
          setLoadingDivision(false);
        }
      }
    };
    
    fetchManagerStats();
    
    // Cleanup function to handle rapid division switching
    return () => {
      isCurrent = false;
    };
  }, [selectedDivision, isAdmin, authLoading, user]);

  // Refresh user stats when division changes (non-admin users)
  useEffect(() => {
    if (isAdmin || authLoading || !user || !selectedDivision) return;
    
    statsApi.getUserStats(selectedDivision)
      .then(data => setUserStats(data))
      .catch(() => {});
    
    // Also fetch pipeline KPIs for employee view
    setPipelineKpisLoading(true);
    approvalApi.getManagerPipelineKpis(user.emp_id, selectedDivision)
      .then(data => setManagerPipelineKpis(data))
      .catch(() => {})
      .finally(() => setPipelineKpisLoading(false));
  }, [selectedDivision, isAdmin, authLoading, user]);

  // Load reviewed managers (admin only)
  useEffect(() => {
    if (!isAdmin || authLoading) return;
    approvalApi.getReviewed().then(data => {
      const map = {};
      for (const r of (data.reviewed || [])) {
        map[r.review_key] = r;
      }
      setReviewedManagers(map);
    }).catch(() => {});
  }, [isAdmin, authLoading]);

  
  // Load customers when admin expands a manager accordion
  const handleManagerAccordionChange = useCallback(async (managerEmpId) => {
    if (!isAdmin || !selectedDivision) return;
    
    // If collapsing (same manager clicked again), just clear
    if (expandedManager === managerEmpId) {
      setExpandedManager(null);
      setManagerPipelineKpis(null);
      return;
    }
    
    setExpandedManager(managerEmpId);
    setLoadingManager(managerEmpId);
    setPipelineKpisLoading(true);
    setManagerPipelineKpis(null);
    setEditingTargets({});
    
    try {
      // Fetch customers and pipeline KPIs in parallel
      const [customersData, kpisData] = await Promise.all([
        customerApi.getAll({ 
          division: selectedDivision, 
          account_manager: managerEmpId
        }),
        approvalApi.getManagerPipelineKpis(managerEmpId, selectedDivision)
      ]);
      setCustomers(customersData);
      setManagerPipelineKpis(kpisData);
    } catch (err) {
      console.error('Failed to fetch manager data:', err);
      toast.error('Failed to load customers');
    } finally {
      setLoadingManager(null);
      setPipelineKpisLoading(false);
    }
  }, [isAdmin, selectedDivision, expandedManager]);

  // Transform API customer data to match UI expectations
  const transformedCustomers = useMemo(() => {
    return customers.map(c => ({
      id: c.composite_id || c.entry_id,  // Use composite_id as the primary identifier for navigation
      compositeId: c.composite_id,
      entryIds: c.entry_ids || [],  // All entry_ids for this customer
      pipelines: c.pipelines,  // All pipelines for this customer
      stages: c.stages || '',  // All stages for this customer
      pipelineCount: c.pipeline_count || 1,
      name: c.name,
      code: c.cust_code,
      division: c.division,
      accountManager: c.account_manager_name,
      accountManagerId: c.account_manager_emp_id,
      lastUpdated: c.updated_at || c.created_at,
      status: c.approved ? 'approved' : c.status,
      approved: c.approved === true || c.status === 'approved',
      approvedBy: c.approved_by,
      approvedAt: c.approved_at,
      thisYearTotal: c.this_year_total || 0,
      lastYearTotal: c.last_year_total || 0,
      lyBudgetTotal: c.ly_budget_total || 0,
      productCount: c.product_count || 0,
      isInvoiceOnly: c.is_invoice_only || false,
      hasNoLines: c.has_no_lines || false,
      isNoCustomer: c.is_no_customer || false,
      isCustomDeal: c.is_custom_deal || false
    }));
  }, [customers]);

  // Filter customers based on search and filters
  const filteredCustomers = useMemo(() => {
    const filtered = transformedCustomers.filter((customer) => {
      const matchesSearch =
        customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.division.toLowerCase().includes(searchQuery.toLowerCase()) ||
        customer.accountManager.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'completed' && customer.status === 'completed') ||
        (statusFilter === 'pending' && customer.status === 'pending') ||
        (statusFilter === 'approved' && customer.status === 'approved');

      const matchesDivision =
        divisionFilter === 'all' || customer.division === divisionFilter;

      const matchesAccountManager =
        accountManagerFilter === 'all' || 
        customer.accountManager === accountManagerFilter ||
        customer.accountManagerId === accountManagerFilter;

      const matchesApproval =
        approvalFilter === 'all' ||
        (approvalFilter === 'approved' && customer.approved) ||
        (approvalFilter === 'unapproved' && !customer.approved);

      const notHiddenCompleted = !hideCompleted || customer.status !== 'completed';
      const notHiddenApproved = !hideApproved || !customer.approved;

      // Also filter by selected division from left panel
      const matchesSelectedDivision = !selectedDivision || customer.division === selectedDivision;

      // Pipeline filter
      const customerPipelines = (customer.pipelines || '').split(',').map(p => p.trim()).filter(Boolean);
      const matchesPipeline = pipelineFilter === 'all' || customerPipelines.includes(pipelineFilter);

      // Stage filter
      const customerStages = (customer.stages || '').split(',').map(s => s.trim()).filter(Boolean);
      const matchesStage = stageFilter === 'all' || customerStages.includes(stageFilter);

      // Hide NEW DEAL + SUSPECT(S) unless toggle is active
      const isNewDealSuspectOnly = customerPipelines.length === 1 && customerPipelines[0] === 'NEW DEAL' &&
        customerStages.every(s => s === 'SUSPECTS' || s === 'SUSPECT');
      const notHiddenSuspect = showNewDealSuspects || !isNewDealSuspectOnly;

      // Hide no-line cards unless toggle is active
      const notHiddenNoLine = showNoLineCards || !customer.hasNoLines;

      return matchesSearch && matchesStatus && matchesDivision && matchesAccountManager && 
             matchesApproval && notHiddenCompleted && notHiddenApproved && matchesSelectedDivision &&
             matchesPipeline && matchesStage && notHiddenSuspect && notHiddenNoLine;
    });
    
    // Apply sorting
    const sorted = [...filtered];
    sorted.sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case 'name': valA = a.name.toLowerCase(); valB = b.name.toLowerCase(); break;
        case 'thisYear': valA = a.thisYearTotal; valB = b.thisYearTotal; break;
        case 'lastYear': valA = a.lastYearTotal; valB = b.lastYearTotal; break;
        case 'lyBudget': valA = a.lyBudgetTotal || 0; valB = b.lyBudgetTotal || 0; break;
        case 'diff': 
          valA = a.lastYearTotal > 0 ? (a.thisYearTotal - a.lastYearTotal) / a.lastYearTotal : 0;
          valB = b.lastYearTotal > 0 ? (b.thisYearTotal - b.lastYearTotal) / b.lastYearTotal : 0;
          break;
        case 'lyBudgetDiff':
          valA = a.lyBudgetTotal > 0 ? (a.thisYearTotal - a.lyBudgetTotal) / a.lyBudgetTotal : -999;
          valB = b.lyBudgetTotal > 0 ? (b.thisYearTotal - b.lyBudgetTotal) / b.lyBudgetTotal : -999;
          break;
        default: valA = a.name.toLowerCase(); valB = b.name.toLowerCase();
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [transformedCustomers, searchQuery, statusFilter, divisionFilter, accountManagerFilter, approvalFilter, 
      hideCompleted, hideApproved, selectedDivision, sortField, sortDirection, pipelineFilter, stageFilter, showNewDealSuspects, showNoLineCards]);

  // Group by division for stats
  const groupCustomersByDivision = useCallback((customerList) => {
    const grouped = {};
    customerList.forEach(customer => {
      if (!grouped[customer.division]) {
        grouped[customer.division] = [];
      }
      grouped[customer.division].push(customer);
    });
    return grouped;
  }, []);

  // Group by account manager
  const groupCustomersByAccountManager = useCallback((customerList) => {
    const grouped = {};
    customerList.forEach(customer => {
      if (!grouped[customer.accountManager]) {
        grouped[customer.accountManager] = [];
      }
      grouped[customer.accountManager].push(customer);
    });
    return grouped;
  }, []);

  // Get all divisions with stats (for left panel) - computed from filtered customers
  // Use transformedCustomers with all filters EXCEPT selectedDivision
  const allDivisionsWithStats = useMemo(() => {
    // Use divisionStats from backend API for both admin and non-admin
    // Backend now correctly filters by user for non-admin
    if (divisionStats.length > 0) {
      return divisionStats.map(ds => ({
        division: ds.division,
        thisYearTotal: ds.ty_budget || 0,
        thisYearAdjusted: ds.ty_budget_adjusted || ds.ty_budget || 0,
        lastYearTotal: ds.ly_budget || 0,
        lyBudgetTotal: ds.ly_budget_total || 0,
        lySaleVsTyPct: ds.ly_sale_vs_ty_pct || 0,
        lyBudgetVsTyPct: ds.ly_budget_vs_ty_pct || 0,
        diffPercent: ds.ly_sale_vs_ty_pct || 0,
        completed: ds.completed_count || 0,
        total: ds.customer_count || 0
      })).sort((a, b) => a.division.localeCompare(b.division));
    }
    
    // Fallback: Compute from filtered customer list (only when divisionStats not yet loaded)
    const baseFiltered = transformedCustomers.filter(customer => {
      const matchesSearch = !searchQuery || customer.name.toLowerCase().includes(searchQuery.toLowerCase()) || customer.code?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || customer.status === statusFilter;
      const matchesDivision = divisionFilter === 'all' || customer.division === divisionFilter;
      const matchesAccountManager = accountManagerFilter === 'all' || customer.accountManager === accountManagerFilter || customer.accountManagerId === accountManagerFilter;
      const matchesApproval = approvalFilter === 'all' || (approvalFilter === 'approved' && customer.approved) || (approvalFilter === 'unapproved' && !customer.approved);
      const notHiddenCompleted = !hideCompleted || customer.status !== 'completed';
      const notHiddenApproved = !hideApproved || !customer.approved;
      const customerPipelines = (customer.pipelines || '').split(',').map(p => p.trim()).filter(Boolean);
      const matchesPipeline = pipelineFilter === 'all' || customerPipelines.includes(pipelineFilter);
      const customerStages = (customer.stages || '').split(',').map(s => s.trim()).filter(Boolean);
      const matchesStage = stageFilter === 'all' || customerStages.includes(stageFilter);
      const isNewDealSuspectOnly = customerPipelines.length === 1 && customerPipelines[0] === 'NEW DEAL' && customerStages.every(s => s === 'SUSPECTS' || s === 'SUSPECT');
      const notHiddenSuspect = showNewDealSuspects || !isNewDealSuspectOnly;
      const notHiddenNoLine = showNoLineCards || !customer.hasNoLines;
      // NOTE: selectedDivision is intentionally NOT applied here
      return matchesSearch && matchesStatus && matchesDivision && matchesAccountManager && matchesApproval && notHiddenCompleted && notHiddenApproved && matchesPipeline && matchesStage && notHiddenSuspect && notHiddenNoLine;
    });

    const grouped = groupCustomersByDivision(baseFiltered);
    return Object.entries(grouped).map(([division, divCustomers]) => {
      const thisYearTotal = divCustomers.reduce((sum, c) => sum + (c.thisYearTotal || 0), 0);
      const lastYearTotal = divCustomers.reduce((sum, c) => sum + (c.lastYearTotal || 0), 0);
      const lyBudgetTotal = divCustomers.reduce((sum, c) => sum + (c.lyBudgetTotal || 0), 0);
      const lySaleVsTyPct = lastYearTotal > 0 ? ((thisYearTotal - lastYearTotal) / lastYearTotal) * 100 : 0;
      const lyBudgetVsTyPct = lyBudgetTotal > 0 ? ((thisYearTotal - lyBudgetTotal) / lyBudgetTotal) * 100 : 0;
      const diffPercent = lySaleVsTyPct;
      const completed = divCustomers.filter(c => c.status === 'completed' || c.status === 'approved').length;
      return { division, thisYearTotal, lastYearTotal, lyBudgetTotal, lySaleVsTyPct, lyBudgetVsTyPct, diffPercent, completed, total: divCustomers.length };
    }).sort((a, b) => a.division.localeCompare(b.division));
  }, [divisionStats, transformedCustomers, searchQuery, statusFilter, divisionFilter, accountManagerFilter, approvalFilter, 
      hideCompleted, hideApproved, pipelineFilter, stageFilter, showNewDealSuspects, showNoLineCards, groupCustomersByDivision]);

  // Get customers grouped by account manager for center panel
  const customersByAccountManager = useMemo(() => {
    return groupCustomersByAccountManager(filteredCustomers);
  }, [filteredCustomers, groupCustomersByAccountManager]);

  // Filter manager cards based on Account Manager filter
  const filteredManagerStats = useMemo(() => {
    let filtered = managerStats;
    
    // Filter by account manager dropdown
    if (accountManagerFilter !== 'all') {
      filtered = filtered.filter(m => m.emp_id === accountManagerFilter);
    }
    
    // Hide managers whose ALL customers are suspect-only (NEW DEAL + SUSPECT/SUSPECTS)
    // unless the "Show Suspect Leads" toggle is active
    if (!showNewDealSuspects) {
      filtered = filtered.filter(m => {
        const totalCount = m.customer_count || 0;
        const suspectOnlyCount = m.suspect_only_count || 0;
        // Keep manager if they have at least 1 non-suspect customer
        return totalCount - suspectOnlyCount > 0;
      });
    }
    
    return filtered;
  }, [managerStats, accountManagerFilter, showNewDealSuspects]);

  // Get selected customers data
  const selectedCustomersData = useMemo(() => {
    return transformedCustomers.filter(c => selectedRows.has(c.id));
  }, [transformedCustomers, selectedRows]);

  // Check if any customers are selected for merge (all customers can be merged now)
  const canMergeCustomers = useMemo(() => {
    return selectedCustomersData.length > 0;
  }, [selectedCustomersData]);

  // Check if selected customers can be deleted (none have REPEAT DEAL pipeline)
  const canDeleteSelectedCustomers = useMemo(() => {
    if (selectedCustomersData.length === 0) return false;
    return selectedCustomersData.every(c => {
      const pls = (c.pipelines || '').split(',').map(p => p.trim()).filter(Boolean);
      return !pls.includes('REPEAT DEAL');
    });
  }, [selectedCustomersData]);

  // Check if selected customers need division change
  const needsDivisionChange = useMemo(() => {
    return selectedCustomersData.some(c => 
      c.division === 'NO DIVISION' || c.division === 'OTHERS' || !c.division
    );
  }, [selectedCustomersData]);

  // Get valid customers for merge target (code starts with CUS)
  const validCustomers = useMemo(() => {
    return transformedCustomers.filter(c => c.code && c.code.toUpperCase().startsWith('CUS') && !selectedRows.has(c.id));
  }, [transformedCustomers, selectedRows]);

  // Valid divisions (excluding NO DIVISION and OTHERS)
  const validDivisions = useMemo(() => {
    return divisions.filter(d => d !== 'NO DIVISION' && d !== 'OTHERS' && d.trim() !== '');
  }, [divisions]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // calculateDiff imported from customer/utils

  // Open pipeline drilldown dialog for a customer
  const handleRowClick = useCallback(async (customerId, isApproved, customer) => {
    if (!customer) return;
    
    const token = sessionStorage.getItem('auth_token');
    const tokenParam = token ? `token=${encodeURIComponent(token)}` : '';
    
    // For approved cards, go directly to budget page in new tab
    if (isApproved) {
      const encodedId = encodeURIComponent(customerId);
      const params = ['locked=true', tokenParam].filter(Boolean).join('&');
      window.open(`/budget/${encodedId}${params ? `?${params}` : ''}`, '_blank');
      return;
    }
    
    // Open drilldown dialog
    setDrilldownCustomer(customer);
    setDrilldownLoading(true);
    setDrilldownPipelines([]);
    
    try {
      const summary = await masterApi.getPipelineSummary(customerId);
      setDrilldownPipelines(summary.pipelines || []);
      setDrilldownCanDeleteAll(summary.can_delete_all || false);
    } catch (err) {
      console.error('Failed to load pipeline summary:', err);
      setDrilldownPipelines([]);
      setDrilldownCanDeleteAll(false);
    } finally {
      setDrilldownLoading(false);
    }
  }, []);

  // Navigate to budget page from drilldown (with optional pipeline filter) — opens new tab
  const handleDrilldownNavigate = useCallback((customerId, isApproved, pipeline) => {
    const encodedId = encodeURIComponent(customerId);
    const token = sessionStorage.getItem('auth_token');
    const params = new URLSearchParams();
    if (isApproved) params.set('locked', 'true');
    if (pipeline) params.set('pipeline', pipeline);
    if (token) params.set('token', token);
    const qs = params.toString();
    window.open(`/budget/${encodedId}${qs ? `?${qs}` : ''}`, '_blank');
  }, []);

  // Delete all items for a specific pipeline from drilldown
  const handleDrilldownDeletePipeline = useCallback(async (compositeId, pipeline) => {
    if (!compositeId || !pipeline) return;
    setDeletingPipeline(pipeline);
    try {
      await masterApi.deletePipelineItems(compositeId, pipeline);
      toast.success(`Deleted all ${pipeline} items`);
      // Refresh pipeline summary
      const summary = await masterApi.getPipelineSummary(compositeId);
      setDrilldownPipelines(summary.pipelines || []);
      setDrilldownCanDeleteAll(summary.can_delete_all || false);
      // If no pipelines left, close dialog
      if (!summary.pipelines || summary.pipelines.length === 0) {
        setDrilldownCustomer(null);
      }
    } catch (err) {
      console.error('Failed to delete pipeline items:', err);
      toast.error(`Failed to delete ${pipeline} items`);
    } finally {
      setDeletingPipeline(null);
    }
  }, []);

  // Delete all non-repeat deal items for a customer (Feature 4)
  const handleDrilldownDeleteAll = useCallback(async (compositeId) => {
    if (!compositeId) return;
    setDeletingPipeline('__all__');
    try {
      await masterApi.deleteAllItems(compositeId);
      toast.success('Deleted all line items for this customer');
      setDrilldownCustomer(null);
    } catch (err) {
      console.error('Failed to delete all items:', err);
      toast.error('Failed to delete items');
    } finally {
      setDeletingPipeline(null);
    }
  }, []);

  // No scroll-back needed — budget page opens in new tab and closes on exit

  // Open Add Deal dialog
  const handleOpenAddDeal = useCallback((division, empId, empName) => {
    setAddDealDivision(division);
    setAddDealEmpId(empId);
    setAddDealEmpName(empName || '');
    setAddDealName('');
    setAddDealIsNoCustomer(false);
    setAddDealLoading(false);
    setShowAddDealDialog(true);
  }, []);

  // Create deal and open budget page
  const handleCreateDeal = useCallback(async () => {
    if (!addDealIsNoCustomer && !addDealName.trim()) {
      toast.error('Please enter a Deal Name');
      return;
    }
    setAddDealLoading(true);
    try {
      const result = await masterApi.createDeal(
        addDealName.trim(),
        addDealDivision,
        addDealEmpId,
        addDealIsNoCustomer
      );
      toast.success(`Deal "${result.deal_name}" created`);
      setShowAddDealDialog(false);
      
      // Refresh customer list
      const customersData = await customerApi.getAll();
      setCustomers(customersData);
      
      // Open budget page in new tab
      const token = sessionStorage.getItem('auth_token');
      const encodedId = encodeURIComponent(result.composite_id);
      const params = token ? `?token=${encodeURIComponent(token)}` : '';
      window.open(`/budget/${encodedId}${params}`, '_blank');
    } catch (err) {
      console.error('Failed to create deal:', err);
      toast.error(`Failed to create deal: ${err.message}`);
    } finally {
      setAddDealLoading(false);
    }
  }, [addDealName, addDealDivision, addDealEmpId, addDealIsNoCustomer]);

  // Approve all budgets for a manager+division (admin only)
  const handleApproveAll = useCallback(async (empId, division) => {
    const key = `${division}|${empId}`;
    setApprovingManager(key);
    try {
      const result = await approvalApi.approveAll(empId, division);
      if (result.approved_count > 0) {
        toast.success(`Approved ${result.approved_count} completed budgets`);
      } else if (result.already_approved > 0) {
        toast.info(`All ${result.already_approved} completed budgets already approved`);
      } else {
        toast.info('No completed budgets to approve');
      }
      // Refresh customers to reflect approval status
      const customersData = await customerApi.getAll();
      setCustomers(customersData);
    } catch (err) {
      toast.error(`Approve failed: ${err.message}`);
    } finally {
      setApprovingManager(null);
    }
  }, []);

  // Mark manager+division as reviewed (admin only)
  const handleReviewManager = useCallback(async (empId, division) => {
    const key = `${division}|${empId}`;
    setReviewingManager(key);
    try {
      const result = await approvalApi.reviewManager(empId, division);
      setReviewedManagers(prev => ({
        ...prev,
        [key]: { review_key: key, reviewed_date: result.reviewed_date, is_today: true }
      }));
      toast.success('Marked as reviewed');
    } catch (err) {
      toast.error(`Review failed: ${err.message}`);
    } finally {
      setReviewingManager(null);
    }
  }, []);

  // Toggle hide reviewed managers
  const toggleHideReviewed = useCallback((val) => {
    setHideReviewed(val);
    sessionStorage.setItem('cl_hideReviewed', val ? 'true' : 'false');
  }, []);

  // Save pipeline target % (admin or employee)
  const handleSavePipelineTarget = useCallback(async (empId, division, pipeline, targetPct) => {
    setSavingTarget(pipeline);
    try {
      await approvalApi.setPipelineTarget(empId, division, pipeline, parseFloat(targetPct));
      // Refresh KPIs
      const kpisData = await approvalApi.getManagerPipelineKpis(empId, division);
      setManagerPipelineKpis(kpisData);
      setEditingTargets(prev => { const n = {...prev}; delete n[pipeline]; return n; });
      toast.success(`Target ${targetPct}% saved for ${pipeline}`);
      // Refresh division stats and manager stats
      if (selectedDivision) {
        lookupApi.getDivisionStats().then(stats => setDivisionStats(stats)).catch(() => {});
        if (isAdmin) {
          lookupApi.getManagerStats(selectedDivision).then(stats => setManagerStats(stats)).catch(() => {});
        }
      }
    } catch (err) {
      toast.error(`Failed to save target: ${err.message}`);
    } finally {
      setSavingTarget(null);
    }
  }, [selectedDivision, isAdmin]);

  // Export data dump for a manager+division
  const handleExportDataDump = useCallback(async (empId, division) => {
    const exportKey = `${division}|${empId}`;
    setExportingManager(exportKey);
    try {
      await exportApi.downloadDataDump(empId, division);
      toast.success('Data dump exported successfully');
    } catch (err) {
      toast.error(`Export failed: ${err.message}`);
    } finally {
      setExportingManager(null);
    }
  }, []);

  // Add new manager (admin only)
  const handleAddManager = useCallback(async () => {
    if (!addManagerSelectedUser || !selectedDivision) return;
    setAddManagerLoading(true);
    try {
      const selectedUser = allUsers.find(u => u.emp_id === addManagerSelectedUser);
      const result = await approvalApi.addManager(selectedUser?.name || addManagerSelectedUser, selectedDivision, addManagerSelectedUser);
      toast.success(`Manager "${result.name}" added to ${selectedDivision}`);
      setShowAddManagerDialog(false);
      setAddManagerSelectedUser('');
      // Refresh manager stats
      const stats = await lookupApi.getManagerStats(selectedDivision);
      setManagerStats(stats);
    } catch (err) {
      toast.error(`Failed to add manager: ${err.message}`);
    } finally {
      setAddManagerLoading(false);
    }
  }, [addManagerSelectedUser, selectedDivision, allUsers]);

  // Add new division (user)
  const handleAddDivision = useCallback(async () => {
    if (!addDivisionSelected) return;
    setAddDivisionLoading(true);
    try {
      await lookupApi.addDivision(addDivisionSelected);
      toast.success(`Division "${addDivisionSelected}" added`);
      setShowAddDivisionDialog(false);
      setAddDivisionSelected('');
      // Refresh division stats
      const stats = await lookupApi.getDivisionStats();
      setDivisionStats(stats);
    } catch (err) {
      toast.error(`Failed to add division: ${err.message}`);
    } finally {
      setAddDivisionLoading(false);
    }
  }, [addDivisionSelected]);



  const handleApproveClick = async (e, customer) => {
    e.stopPropagation();
    setPendingApproval(customer);
    setConfirmDialogOpen(true);
  };

  const handleConfirmApprove = async () => {
    if (!pendingApproval || !user) return;
    
    try {
      await approvalApi.approve(
        pendingApproval.id,
        user.emp_id,
        user.name
      );
      
      // Update local state - match by composite_id
      setCustomers(prev => prev.map(c => 
        c.composite_id === pendingApproval.id 
          ? { ...c, approved: true, status: 'approved', approved_by: user.emp_id }
          : c
      ));
      
      toast.success(`Budget approved for ${pendingApproval.name}. Screen is now locked for editing.`);
    } catch (err) {
      console.error('Approval failed:', err);
      toast.error(`Failed to approve: ${err.message}`);
    }
    
    setConfirmDialogOpen(false);
    setPendingApproval(null);
  };

  // Row selection handlers
  const handleRowSelect = useCallback((customerId, e) => {
    e.stopPropagation();
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(customerId)) {
        newSet.delete(customerId);
      } else {
        newSet.add(customerId);
      }
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback((customerList) => {
    const ids = customerList.filter(c => !c.approved).map(c => c.id);
    setSelectedRows(prev => {
      const allSelected = ids.every(id => prev.has(id));
      if (allSelected) {
        const newSet = new Set(prev);
        ids.forEach(id => newSet.delete(id));
        return newSet;
      } else {
        return new Set([...prev, ...ids]);
      }
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedRows(new Set());
  }, []);

  // Bulk action: Merge customers into a target customer
  const handleMergeCustomers = async () => {
    if (mergeMode === 'mother') {
      // Mother Customer mode - name is used as both name and code
      if (!motherName.trim()) {
        toast.error('Please enter a name for the Mother Customer');
        return;
      }
      if (selectedCustomersData.length < 2) {
        toast.error('Select at least 2 customers to create a Mother Customer');
        return;
      }
      try {
        const sourceIds = selectedCustomersData.map(c => c.compositeId);
        const result = await customerApi.createMother(sourceIds, motherName.trim());
        toast.success(result.message);
        
        // Refresh division stats (accounts for merged customer count changes)
        const freshDivStats = await lookupApi.getDivisionStats();
        setDivisionStats(freshDivStats);
        
        if (isAdmin && selectedDivision) {
          const managerStatsData = await lookupApi.getManagerStats(selectedDivision);
          setManagerStats(managerStatsData);
        } else {
          const customersData = await customerApi.getAll();
          setCustomers(customersData);
        }
        
        setShowMergeDialog(false);
        setMotherName('');
        setMergeMode('existing');
        clearSelection();
      } catch (err) {
        console.error('Mother customer creation failed:', err);
        toast.error(`Failed: ${err.message}`);
      }
      return;
    }
    
    // Existing merge mode
    if (!mergeTargetCustomer) {
      toast.error('Please select a target customer');
      return;
    }
    
    // Get composite IDs of all selected customers (except the target)
    const customersToMerge = selectedCustomersData
      .filter(c => c.compositeId !== mergeTargetCustomer)
      .map(c => c.compositeId);
    
    if (customersToMerge.length === 0) {
      toast.error('No customers to merge (target customer cannot merge into itself)');
      return;
    }
    
    try {
      const result = await customerApi.merge(customersToMerge, mergeTargetCustomer);
      toast.success(result.message);
      
      // Refresh division stats (accounts for merged customer count changes)
      const freshDivStats = await lookupApi.getDivisionStats();
      setDivisionStats(freshDivStats);
      
      // Refresh data based on current view
      if (isAdmin && selectedDivision) {
        const managerStatsData = await lookupApi.getManagerStats(selectedDivision);
        setManagerStats(managerStatsData);
      } else {
        const customersData = await customerApi.getAll();
        setCustomers(customersData);
      }
      
      setShowMergeDialog(false);
      setMergeTargetCustomer('');
      clearSelection();
    } catch (err) {
      console.error('Merge failed:', err);
      toast.error(`Merge failed: ${err.message}`);
    }
  };

  // Bulk action: Transfer customer to another manager
  const handleTransferCustomer = async () => {
    if (!transferManager) {
      toast.error('Please select a manager');
      return;
    }
    
    const manager = allAccountManagers.find(m => m.emp_id === transferManager);
    if (!manager) {
      toast.error('Manager not found');
      return;
    }
    
    try {
      for (const customer of selectedCustomersData) {
        if (!customer.approved) {
          await customerApi.transfer(customer.compositeId, manager.emp_id, manager.name);
        }
      }
      
      toast.success(`Transferred ${selectedCustomersData.filter(c => !c.approved).length} customer(s) to ${manager.name}`);
      
      // Refresh division stats and data
      const freshDivStats = await lookupApi.getDivisionStats();
      setDivisionStats(freshDivStats);
      
      if (isAdmin && selectedDivision) {
        const managerStatsData = await lookupApi.getManagerStats(selectedDivision);
        setManagerStats(managerStatsData);
      } else {
        const customersData = await customerApi.getAll();
        setCustomers(customersData);
      }
      
      setShowTransferDialog(false);
      setTransferManager('');
      clearSelection();
    } catch (err) {
      console.error('Transfer failed:', err);
      toast.error(`Transfer failed: ${err.message}`);
    }
  };

  // Bulk action: Update division
  const handleUpdateDivision = async () => {
    if (!newDivision) {
      toast.error('Please select a division');
      return;
    }
    
    try {
      for (const customer of selectedCustomersData) {
        if (!customer.approved && (customer.division === 'NO DIVISION' || customer.division === 'OTHERS' || !customer.division)) {
          await customerApi.updateDivision(customer.id, newDivision);
        }
      }
      
      const updated = selectedCustomersData.filter(c => !c.approved && (c.division === 'NO DIVISION' || c.division === 'OTHERS' || !c.division)).length;
      toast.success(`Updated division for ${updated} customer(s)`);
      
      // Refresh data
      const [customersData, divisionsData] = await Promise.all([
        customerApi.getAll(),
        lookupApi.getDivisions()
      ]);
      setCustomers(customersData);
      setDivisions(divisionsData);
      
      setShowDivisionDialog(false);
      setNewDivision('');
      clearSelection();
    } catch (err) {
      console.error('Division update failed:', err);
      toast.error(`Division update failed: ${err.message}`);
    }
  };

  // Bulk delete selected customers (all their non-REPEAT DEAL items)
  const handleDeleteSelectedCustomers = async () => {
    const toDelete = selectedCustomersData.filter(c => {
      const pls = (c.pipelines || '').split(',').map(p => p.trim()).filter(Boolean);
      return !pls.includes('REPEAT DEAL');
    });
    
    if (toDelete.length === 0) {
      toast.error('No customers without REPEAT DEAL selected');
      return;
    }
    
    try {
      let totalDeleted = 0;
      for (const customer of toDelete) {
        const result = await masterApi.deleteAllItems(customer.id);
        totalDeleted += result.deleted_count || 0;
      }
      toast.success(`Deleted all line items for ${toDelete.length} customer(s) (${totalDeleted} items)`);
      
      // Refresh data
      const customersData = await customerApi.getAll();
      setCustomers(customersData);
      clearSelection();
    } catch (err) {
      console.error('Customer deletion failed:', err);
      toast.error(`Deletion failed: ${err.message}`);
    }
  };

  // Note: getDivisionColor, getAccountManagerStats, calculateDiff imported from customer/utils

  const clearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setDivisionFilter('all');
    setAccountManagerFilter('all');
    setApprovalFilter('all');
    setHideCompleted(false);
    setHideApproved(false);
    setPipelineFilter('all');
    setStageFilter('all');
    setShowNewDealSuspects(false);
    setSortField('name');
    setSortDirection('asc');
  };

  // Sorting handler
  const handleSort = useCallback((field) => {
    setSortDirection(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortField(field);
  }, [sortField]);

  // Note: Initial division selection is handled in the main fetchData useEffect
  // Removed duplicate useEffect to avoid race conditions when switching divisions

  // Loading state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-500">Authenticating...</p>
        </div>
      </div>
    );
  }

  // Auth error or no user
  if (authError || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <Shield className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Required</h2>
          <p className="text-gray-500 mb-4">
            {authError || 'Please login from the main application to access this dashboard.'}
          </p>
          <p className="text-xs text-gray-400">
            Token required in URL: <code className="bg-gray-100 px-1 rounded">?token=xxx</code>
          </p>
        </div>
      </div>
    );
  }

  // No data
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <Shield className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-500 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  // No customers found - but for admin with lazy loading, check managerStats instead
  if (!isAdmin && !loadingCustomers && !loadingDivisions && transformedCustomers.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Customers Found</h2>
          <p className="text-gray-500 mb-4">
            No budget data has been pushed yet. Please use the <code className="bg-gray-100 px-1 rounded">/api/master/push</code> API to upload customer and product data.
          </p>
        </div>
      </div>
    );
  }
  
  // For admin: check if managerStats is empty (no data in selected division) - but not while loading
  // NOTE: We no longer early-return here - instead we show the empty state inside the main layout
  // so the user can still access the division panel to select a different division
  const showNoManagersMessage = isAdmin && managerStats.length === 0 && !loadingCustomers && !loadingDivisions && !loadingDivision;

  const currentUserName = user?.name || '';

  // Render bulk action bar
  const renderBulkActionBar = () => {
    if (selectedRows.size === 0) return null;
    
    return (
      <div className="bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-blue-800">{selectedRows.size} selected</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs text-blue-600 hover:bg-blue-100"
            onClick={clearSelection}
          >
            <X className="h-3 w-3 mr-1" />
            Clear
          </Button>
        </div>
        <div className="flex items-center gap-2">
          {/* Merge - For invalid codes (doesn't start with CUS) */}
          {canMergeCustomers && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-purple-300 text-purple-700 hover:bg-purple-50"
              onClick={() => {
                console.log('Merge Customer button clicked!');
                setShowMergeDialog(true);
              }}
            >
              <Merge className="h-3 w-3 mr-1" />
              Merge Customer
            </Button>
          )}
          
          {/* Division Change - Only for NO DIVISION or OTHERS */}
          {needsDivisionChange && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-orange-300 text-orange-700 hover:bg-orange-50"
              onClick={() => setShowDivisionDialog(true)}
            >
              <FolderEdit className="h-3 w-3 mr-1" />
              Change Division
            </Button>
          )}
          
          {/* Transfer */}
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs border-blue-300 text-blue-700 hover:bg-blue-50"
            onClick={() => setShowTransferDialog(true)}
            disabled={selectedCustomersData.every(c => c.approved)}
          >
            <UserPlus className="h-3 w-3 mr-1" />
            Transfer
          </Button>

          {/* Delete Customer - only if none have REPEAT DEAL */}
          {canDeleteSelectedCustomers && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-50"
              onClick={handleDeleteSelectedCustomers}
              data-testid="bulk-delete-customers"
            >
              <Trash2 className="h-3 w-3 mr-1" />
              Delete Customer
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Sort icon helper
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  // Render customer table
  const renderCustomerTable = (customerList, isApprovedSection = false) => (
    <table className="w-full">
      <thead>
        <tr className="bg-gray-50 text-[10px] font-semibold text-gray-500 uppercase">
          <th className="px-2 py-2 text-center w-10">
            <Checkbox 
              checked={customerList.filter(c => !c.approved).every(c => selectedRows.has(c.id)) && customerList.filter(c => !c.approved).length > 0}
              onCheckedChange={() => handleSelectAll(customerList)}
              data-testid="select-all-checkbox"
            />
          </th>
          <th className="px-4 py-2 text-left cursor-pointer select-none hover:text-gray-700" onClick={() => handleSort('name')} data-testid="sort-name">
            <span className="inline-flex items-center">Customer Name <SortIcon field="name" /></span>
          </th>
          <th className="px-3 py-2 text-right cursor-pointer select-none hover:text-gray-700" onClick={() => handleSort('lyBudget')} data-testid="sort-ly-budget">
            <span className="inline-flex items-center justify-end">LY Budget <SortIcon field="lyBudget" /></span>
          </th>
          <th className="px-3 py-2 text-right cursor-pointer select-none hover:text-gray-700" onClick={() => handleSort('lastYear')} data-testid="sort-ly-sale">
            <span className="inline-flex items-center justify-end">LY Sale <SortIcon field="lastYear" /></span>
          </th>
          <th className="px-3 py-2 text-right cursor-pointer select-none hover:text-gray-700" onClick={() => handleSort('thisYear')} data-testid="sort-ty">
            <span className="inline-flex items-center justify-end">TY Budget <SortIcon field="thisYear" /></span>
          </th>
          <th className="px-2 py-2 text-center cursor-pointer select-none hover:text-gray-700" onClick={() => handleSort('lyBudgetDiff')} data-testid="sort-bgt-diff" title="LY Budget vs TY Budget %">
            <span className="inline-flex items-center">Bgt % <SortIcon field="lyBudgetDiff" /></span>
          </th>
          <th className="px-2 py-2 text-center cursor-pointer select-none hover:text-gray-700" onClick={() => handleSort('diff')} data-testid="sort-sale-diff" title="LY Sale vs TY Budget %">
            <span className="inline-flex items-center">Sale % <SortIcon field="diff" /></span>
          </th>
          <th className="px-4 py-2 text-center">Status</th>
          <th className="px-4 py-2 text-right">Action</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {customerList.map((customer, idx) => {
          const lySaleVsTyPct = calculateDiff(customer.thisYearTotal, customer.lastYearTotal);
          const lyBudgetVsTyPct = customer.lyBudgetTotal > 0 
            ? ((customer.thisYearTotal - customer.lyBudgetTotal) / customer.lyBudgetTotal) * 100 
            : null;
          const isPositiveSale = lySaleVsTyPct >= 0;
          const isPositiveBgt = lyBudgetVsTyPct !== null ? lyBudgetVsTyPct >= 0 : true;
          const isSelected = selectedRows.has(customer.id);
          const isInvalidCode = !customer.isCustomDeal && (!customer.code || !customer.code.toUpperCase().startsWith('CUS'));
          const needsDivision = customer.division === 'NO DIVISION' || customer.division === 'OTHERS' || !customer.division;
          const isInvoiceOnly = customer.isInvoiceOnly;
          
          return (
            <tr
              key={`${customer.id}-${idx}`}
              onClick={() => handleRowClick(customer.id, customer.approved, customer)}
              className={`group transition-colors cursor-pointer ${
                customer.isCustomDeal ? 'bg-indigo-50/50 hover:bg-indigo-100/50 border-l-2 border-l-indigo-400' :
                customer.hasNoLines ? 'bg-gray-50/80 hover:bg-gray-100/50 opacity-60' :
                isSelected ? 'bg-blue-50 hover:bg-blue-100' :
                isInvoiceOnly ? 'bg-amber-50/50 hover:bg-amber-100/50' :
                customer.approved ? 'bg-green-50/50 hover:bg-green-100/50' 
                : customer.status === 'completed' ? 'bg-green-50/30 hover:bg-green-50'
                : 'bg-white hover:bg-gray-50'
              }`}
              data-testid={`customer-row-${customer.id}`}
              data-customer-id={customer.id}
            >
              <td className="px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                <Checkbox 
                  checked={isSelected}
                  onCheckedChange={(e) => handleRowSelect(customer.id, { stopPropagation: () => {} })}
                  disabled={customer.approved}
                  data-testid={`select-checkbox-${customer.id}`}
                />
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    isInvalidCode ? 'bg-red-500' : 'bg-zinc-900'
                  }`}>
                    <span className="text-[10px] font-bold text-white">
                      {customer.name.split(' ').map(w => w[0]).slice(0, 2).join('')}
                    </span>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-medium text-gray-900 text-sm">{customer.name}</span>
                    <div className="flex gap-1 mt-0.5 flex-wrap">
                      {customer.pipelines && customer.pipelines.split(',').map(p => p.trim()).filter(Boolean).map(p => (
                        <Badge key={p} className={`text-[9px] h-4 px-1 border-0 ${
                          p === 'NEW DEAL' ? 'bg-blue-100 text-blue-700' :
                          p === 'CROSS SELLING' ? 'bg-purple-100 text-purple-700' :
                          p === 'UP SELLING' ? 'bg-indigo-100 text-indigo-700' :
                          p === 'WIN BACK' ? 'bg-orange-100 text-orange-700' :
                          p === 'REPEAT DEAL' ? 'bg-emerald-100 text-emerald-700' :
                          p === 'INDENTING DEAL' ? 'bg-teal-100 text-teal-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>{p}</Badge>
                      ))}
                      {customer.stages && customer.stages.split(',').map(s => s.trim()).filter(Boolean).map(s => (
                        <Badge key={s} variant="outline" className={`text-[9px] h-4 px-1 ${
                          s === 'SUSPECT' || s === 'SUSPECTS' ? 'border-yellow-400 text-yellow-700 bg-yellow-50' :
                          s === 'WON' ? 'border-green-400 text-green-700 bg-green-50' :
                          s === 'LOST' ? 'border-red-400 text-red-700 bg-red-50' :
                          s === 'CLOSING' ? 'border-emerald-400 text-emerald-700 bg-emerald-50' :
                          'border-gray-300 text-gray-600'
                        }`}>{s}</Badge>
                      ))}
                      {isInvoiceOnly && <Badge className="text-[9px] h-4 px-1 bg-amber-100 text-amber-700 border-0">Invoice Only</Badge>}
                      {customer.isCustomDeal && <Badge className="text-[9px] h-4 px-1 bg-indigo-100 text-indigo-700 border-0">New Deal</Badge>}
                      {customer.hasNoLines && <Badge className="text-[9px] h-4 px-1 bg-gray-200 text-gray-500 border-0">No Lines</Badge>}
                      {isInvalidCode && <Badge variant="destructive" className="text-[9px] h-4 px-1">Invalid Code</Badge>}
                      {needsDivision && <Badge variant="outline" className="text-[9px] h-4 px-1 border-orange-300 text-orange-600">{customer.division || 'No Division'}</Badge>}
                    </div>
                  </div>
                  {customer.approved && <Lock className="h-3 w-3 text-green-600" />}
                </div>
              </td>
              <td className="px-3 py-3 text-right">
                <span className="font-mono text-xs text-gray-400">{customer.lyBudgetTotal > 0 ? formatSmartCurrency(customer.lyBudgetTotal) : '-'}</span>
              </td>
              <td className="px-3 py-3 text-right">
                <span className="font-mono text-xs text-gray-500">{formatSmartCurrency(customer.lastYearTotal)}</span>
              </td>
              <td className="px-3 py-3 text-right">
                <span className="font-mono text-sm font-semibold">{formatSmartCurrency(customer.thisYearTotal)}</span>
              </td>
              <td className="px-2 py-3 text-center">
                {lyBudgetVsTyPct !== null ? (
                  <div className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                    isPositiveBgt ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'
                  }`} title="LY Budget vs TY Budget">
                    {isPositiveBgt ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
                    {isPositiveBgt ? '+' : ''}{lyBudgetVsTyPct.toFixed(1)}%
                  </div>
                ) : <span className="text-[10px] text-gray-300">-</span>}
              </td>
              <td className="px-2 py-3 text-center">
                <div className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                  isPositiveSale ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                }`} title="LY Sale vs TY Budget">
                  {isPositiveSale ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
                  {isPositiveSale ? '+' : ''}{lySaleVsTyPct.toFixed(1)}%
                </div>
              </td>
              <td className="px-4 py-3 text-center">
                {customer.approved ? (
                  <Badge className="bg-green-100 text-green-800 border-0 text-[10px]">
                    <ShieldCheck className="h-2.5 w-2.5 mr-0.5" />
                    Approved
                  </Badge>
                ) : customer.status === 'completed' ? (
                  <Badge className="bg-green-100 text-green-800 border-0 text-[10px]">
                    <Check className="h-2.5 w-2.5 mr-0.5" />
                    Completed
                  </Badge>
                ) : (
                  <Badge variant="secondary" className="bg-gray-100 text-gray-700 border-0 text-[10px]">
                    Pending
                  </Badge>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                {customer.approved ? (
                  <span className="text-[10px] text-green-600 font-medium flex items-center justify-end gap-1">
                    <Lock className="h-3 w-3" />
                    View Only
                  </span>
                ) : customer.status === 'completed' && !isApprovedSection && isAdmin ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs border-green-300 text-green-700 hover:bg-green-50"
                    onClick={(e) => handleApproveClick(e, customer)}
                  >
                    <ShieldCheck className="h-3 w-3 mr-1" />
                    Approve
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 h-7 text-xs"
                  >
                    Open
                    <ChevronRight className="h-3 w-3 ml-1" />
                  </Button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  // Left Panel - Divisions
  // Left Panel - Divisions (extracted to DivisionPanel component)
  const renderLeftPanel = () => (
    <DivisionPanel
      isOpen={leftPanelOpen}
      setIsOpen={setLeftPanelOpen}
      divisions={allDivisionsWithStats}
      selectedDivision={selectedDivision}
      onSelectDivision={setSelectedDivision}
      loading={loadingDivisions}
    />
  );

  // Right Panel - Filters (extracted to FilterPanel component)
  const renderRightPanel = () => (
    <FilterPanel
      isOpen={rightPanelOpen}
      setIsOpen={setRightPanelOpen}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      statusFilter={statusFilter}
      setStatusFilter={setStatusFilter}
      approvalFilter={approvalFilter}
      setApprovalFilter={setApprovalFilter}
      accountManagerFilter={accountManagerFilter}
      setAccountManagerFilter={setAccountManagerFilter}
      hideCompleted={hideCompleted}
      setHideCompleted={setHideCompleted}
      hideApproved={hideApproved}
      setHideApproved={setHideApproved}
      pipelineFilter={pipelineFilter}
      setPipelineFilter={setPipelineFilter}
      stageFilter={stageFilter}
      setStageFilter={setStageFilter}
      showNewDealSuspects={showNewDealSuspects}
      setShowNewDealSuspects={setShowNewDealSuspects}
      showNoLineCards={showNoLineCards}
      setShowNoLineCards={setShowNoLineCards}
      isAdmin={isAdmin}
      managerStats={managerStats}
      clearFilters={clearFilters}
    />
  );

  // Pipeline Drilldown Dialog
  const renderDrilldownDialog = () => (
    <Dialog open={!!drilldownCustomer} onOpenChange={(open) => !open && setDrilldownCustomer(null)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-zinc-900 flex items-center justify-center">
              <span className="text-[10px] font-bold text-white">
                {drilldownCustomer?.name?.split(' ').map(w => w[0]).slice(0, 2).join('')}
              </span>
            </div>
            <div>
              <div className="text-sm font-semibold">{drilldownCustomer?.name}</div>
              <div className="text-[10px] text-gray-500 font-normal">{drilldownCustomer?.division} &middot; {drilldownCustomer?.code}</div>
            </div>
          </DialogTitle>
          <DialogDescription className="text-xs">
            Pipeline breakdown &middot; Click a pipeline to open, or delete its line items.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          {drilldownLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Loading pipelines...</span>
            </div>
          ) : drilldownPipelines.length === 0 ? (
            <div className="text-center py-6">
              <Package className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No line items found</p>
              <Button
                size="sm"
                className="mt-3"
                onClick={() => {
                  setDrilldownCustomer(null);
                  handleDrilldownNavigate(drilldownCustomer?.id, drilldownCustomer?.approved);
                }}
              >
                Open Budget Page
              </Button>
            </div>
          ) : (
            <div className="space-y-1.5">
              {drilldownPipelines.map(p => (
                <div key={p.pipeline} className="flex items-center justify-between px-3 py-2 rounded-lg border border-gray-100 hover:bg-gray-50 group/item">
                  <button
                    className="flex items-center gap-2 flex-1 text-left"
                    onClick={() => {
                      setDrilldownCustomer(null);
                      handleDrilldownNavigate(drilldownCustomer?.id, drilldownCustomer?.approved, p.pipeline);
                    }}
                    data-testid={`drilldown-pipeline-${p.pipeline.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <Badge className={`text-[9px] h-5 px-1.5 border-0 ${
                      p.pipeline === 'NEW DEAL' ? 'bg-blue-100 text-blue-700' :
                      p.pipeline === 'CROSS SELLING' ? 'bg-purple-100 text-purple-700' :
                      p.pipeline === 'UP SELLING' ? 'bg-indigo-100 text-indigo-700' :
                      p.pipeline === 'WIN BACK' ? 'bg-orange-100 text-orange-700' :
                      p.pipeline === 'REPEAT DEAL' ? 'bg-emerald-100 text-emerald-700' :
                      p.pipeline === 'INDENTING DEAL' ? 'bg-teal-100 text-teal-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{p.pipeline}</Badge>
                    <span className="text-xs text-gray-600 font-medium">{p.count} {p.count === 1 ? 'item' : 'items'}{p.count === 0 ? ' (empty)' : ''}</span>
                    <ExternalLink className="h-3 w-3 text-gray-400 opacity-0 group-hover/item:opacity-100" />
                  </button>
                  {p.deletable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover/item:opacity-100"
                      onClick={() => handleDrilldownDeletePipeline(drilldownCustomer?.id, p.pipeline)}
                      disabled={deletingPipeline !== null}
                      data-testid={`drilldown-delete-${p.pipeline.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {deletingPipeline === p.pipeline ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
          {drilldownCanDeleteAll && drilldownPipelines.length > 0 && (
            <Button
              variant="destructive"
              size="sm"
              className="text-xs"
              onClick={() => handleDrilldownDeleteAll(drilldownCustomer?.id)}
              disabled={deletingPipeline !== null}
              data-testid="drilldown-delete-all"
            >
              {deletingPipeline === '__all__' ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 mr-1" />}
              Delete All Lines
            </Button>
          )}
          <Button
            size="sm"
            className="text-xs ml-auto"
            onClick={() => {
              setDrilldownCustomer(null);
              handleDrilldownNavigate(drilldownCustomer?.id, drilldownCustomer?.approved);
            }}
            data-testid="drilldown-open-budget"
          >
            Open Budget Page
            <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Add Deal Dialog
  const renderAddDealDialog = () => (
    <Dialog open={showAddDealDialog} onOpenChange={(open) => !open && setShowAddDealDialog(false)}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">Add New Deal</DialogTitle>
          <DialogDescription className="text-xs">
            {addDealDivision} &middot; {addDealEmpName || addDealEmpId}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs font-medium text-gray-700 mb-1 block">Deal Name</label>
            <Input
              value={addDealName}
              onChange={(e) => setAddDealName(e.target.value)}
              placeholder="Enter customer / deal name"
              disabled={addDealIsNoCustomer}
              className="h-9 text-sm"
              data-testid="add-deal-name"
              autoFocus
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              checked={addDealIsNoCustomer}
              onCheckedChange={(checked) => {
                setAddDealIsNoCustomer(!!checked);
                if (checked) setAddDealName('');
              }}
              id="no-customer-checkbox"
              data-testid="add-deal-no-customer"
            />
            <label htmlFor="no-customer-checkbox" className="text-xs text-gray-600 cursor-pointer select-none">
              No Customer (budget without a specific customer)
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddDealDialog(false)}
            className="text-xs"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleCreateDeal}
            disabled={addDealLoading || (!addDealIsNoCustomer && !addDealName.trim())}
            className="text-xs"
            data-testid="add-deal-create"
          >
            {addDealLoading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
            Create Budget
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Main render - Admin view with account manager accordions
  if (isAdmin) {
    return (
      <div className="h-[calc(100vh-57px)] flex overflow-hidden" data-testid="customer-list-page">
        {/* Confirmation Dialog */}
        <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Confirm Budget Approval</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to approve the budget for <strong>{pendingApproval?.name}</strong>?
                <br /><br />
                This will lock the customer from further editing.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmApprove} className="bg-green-600 hover:bg-green-700">
                <ShieldCheck className="h-4 w-4 mr-2" />
                Approve & Lock
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Pipeline Drilldown Dialog */}
        {renderDrilldownDialog()}

        {/* Add Deal Dialog */}
        {renderAddDealDialog()}

        {/* Merge Dialog for Admin View */}
        <AlertDialog open={showMergeDialog} onOpenChange={(open) => {
          setShowMergeDialog(open);
          if (!open) {
            setMergeSearchQuery('');
            setMergeMode('existing');
            setMotherName('');
          }
        }}>
          <AlertDialogContent className="max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle>Merge Customers</AlertDialogTitle>
              <AlertDialogDescription>
                Merge <strong>{selectedCustomersData.length}</strong> selected customer(s).
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-2">
              {/* Mode toggle */}
              <div className="flex gap-2 mb-3">
                <Button 
                  variant={mergeMode === 'existing' ? 'default' : 'outline'} size="sm"
                  onClick={() => setMergeMode('existing')}
                  data-testid="merge-mode-existing"
                >Merge into Existing</Button>
                <Button 
                  variant={mergeMode === 'mother' ? 'default' : 'outline'} size="sm"
                  onClick={() => setMergeMode('mother')}
                  data-testid="merge-mode-mother"
                >Create Mother Customer</Button>
              </div>
              
              {mergeMode === 'mother' ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600">Mother Customer Name</label>
                    <Input
                      placeholder="e.g. ACME GROUP"
                      value={motherName}
                      onChange={(e) => setMotherName(e.target.value)}
                      data-testid="mother-name-input"
                    />
                    <p className="text-xs text-gray-400 mt-1">This will be used as both the name and code</p>
                  </div>
                  <div className="bg-blue-50 rounded p-2 text-xs text-blue-700">
                    This will create a new Mother Customer and transfer all budget data from the {selectedCustomersData.length} selected customers into it. Source customers will be hidden from the list.
                  </div>
                </div>
              ) : (
                <>
                  {loadingMergeTargets ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-gray-400 mr-2" />
                      <span className="text-sm text-gray-500">Loading customers...</span>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Input
                        placeholder="Search customers by name or code..."
                        value={mergeSearchQuery || ''}
                        onChange={(e) => setMergeSearchQuery(e.target.value)}
                        className="w-full"
                      />
                      <div className="max-h-48 overflow-y-auto border rounded-md">
                        {mergeTargets
                          .filter(c => {
                            if (!mergeSearchQuery) return true;
                            const q = mergeSearchQuery.toLowerCase().trim();
                            return ((c.name || '').toLowerCase().trim().includes(q) || 
                                   (c.cust_code || '').toLowerCase().trim().includes(q));
                          })
                          .slice(0, 100)
                          .map(c => (
                            <div
                              key={c.cust_code}
                              onClick={() => setMergeTargetCustomer(c.cust_code)}
                              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 border-b last:border-b-0 text-sm ${
                                mergeTargetCustomer === c.cust_code ? 'bg-blue-50 text-blue-700' : ''
                              }`}
                            >
                              <div className="font-medium">{(c.name || '').trim()}</div>
                              <div className="text-xs text-gray-500">{c.cust_code}</div>
                            </div>
                          ))}
                        {mergeTargets.filter(c => {
                          if (!mergeSearchQuery) return true;
                          const q = mergeSearchQuery.toLowerCase().trim();
                          return ((c.name || '').toLowerCase().trim().includes(q) || 
                                 (c.cust_code || '').toLowerCase().trim().includes(q));
                        }).length === 0 && (
                          <div className="px-3 py-4 text-center text-gray-500 text-sm">
                            No customers found
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-400">
                        {mergeSearchQuery ? 'Showing first 100 matches' : `${mergeTargets.length} customers available - type to search`}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setShowMergeDialog(false)}>Cancel</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleMergeCustomers} 
                disabled={mergeMode === 'existing' ? (!mergeTargetCustomer || loadingMergeTargets) : !motherName.trim()} 
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Merge className="h-4 w-4 mr-2" />
                {mergeMode === 'mother' ? 'Create Mother' : 'Merge'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Transfer Dialog */}
        <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Transfer Customer to Another Manager</DialogTitle>
              <DialogDescription>
                Transfer {selectedCustomersData.filter(c => !c.approved).length} customer(s) to a different account manager.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Select value={transferManager || undefined} onValueChange={setTransferManager}>
                <SelectTrigger>
                  <SelectValue placeholder="Select account manager" />
                </SelectTrigger>
                <SelectContent>
                  {allAccountManagers.filter(m => m.emp_id).map(m => (
                    <SelectItem key={m.emp_id} value={m.emp_id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTransferDialog(false)}>Cancel</Button>
              <Button onClick={handleTransferCustomer} disabled={!transferManager} className="bg-blue-600 hover:bg-blue-700">
                <UserPlus className="h-4 w-4 mr-2" />
                Transfer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Division Change Dialog */}
        <Dialog open={showDivisionDialog} onOpenChange={setShowDivisionDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Division</DialogTitle>
              <DialogDescription>
                Update division for {selectedCustomersData.filter(c => !c.approved && (c.division === 'NO DIVISION' || c.division === 'OTHERS' || !c.division)).length} customer(s).
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Select value={newDivision} onValueChange={setNewDivision}>
                <SelectTrigger>
                  <SelectValue placeholder="Select division" />
                </SelectTrigger>
                <SelectContent>
                  {validDivisions.map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDivisionDialog(false)}>Cancel</Button>
              <Button onClick={handleUpdateDivision} disabled={!newDivision} className="bg-orange-600 hover:bg-orange-700">
                <FolderEdit className="h-4 w-4 mr-2" />
                Update Division
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Left Panel */}
        {renderLeftPanel()}

        {/* Center Panel */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Header */}
          {selectedDivision && (
            <div className={`${getDivisionColor(selectedDivision)} px-4 py-2 flex items-center justify-between`}>
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-white" />
                <div>
                  <h1 className="text-white font-bold">{selectedDivision}</h1>
                  <p className="text-white/70 text-xs">
                    {accountManagerFilter === 'all' 
                      ? `${filteredManagerStats.length} managers • ${filteredManagerStats.reduce((sum, m) => sum + Math.max(0, (m.customer_count || 0) - (!showNewDealSuspects ? (m.suspect_only_count || 0) : 0)), 0)} customers`
                      : `Filtered: ${filteredManagerStats.length} manager • ${filteredManagerStats.reduce((sum, m) => sum + Math.max(0, (m.customer_count || 0) - (!showNewDealSuspects ? (m.suspect_only_count || 0) : 0)), 0)} customers`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {(() => {
                  const divStats = allDivisionsWithStats.find(d => d.division === selectedDivision);
                  if (!divStats) return null;
                  const hasLyBudget = (divStats.lyBudgetTotal || 0) > 0;
                  return (
                    <>
                      {hasLyBudget && (
                        <div className="text-right">
                          <p className="text-[8px] text-white/50 uppercase">LY Budget</p>
                          <p className="font-mono text-xs text-white/70">{formatSmartCurrency(divStats.lyBudgetTotal)}</p>
                        </div>
                      )}
                      <div className="text-right">
                        <p className="text-[8px] text-white/50 uppercase">LY Sale</p>
                        <p className="font-mono text-xs text-white/70">{formatSmartCurrency(divStats.lastYearTotal)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[8px] text-white/50 uppercase">TY Budget</p>
                        <p className="font-mono text-sm font-semibold text-white">{formatSmartCurrency(divStats.thisYearTotal)}</p>
                      </div>
                      {hasLyBudget && (
                        <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-white/20 ${
                          (divStats.lyBudgetVsTyPct || 0) >= 0 ? 'text-blue-200' : 'text-orange-200'
                        }`} title="LY Budget vs TY Budget">
                          {(divStats.lyBudgetVsTyPct || 0) >= 0 ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
                          {(divStats.lyBudgetVsTyPct || 0) >= 0 ? '+' : ''}{(divStats.lyBudgetVsTyPct || 0).toFixed(1)}%
                        </div>
                      )}
                      <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-white/20 ${
                        (divStats.lySaleVsTyPct || 0) >= 0 ? 'text-green-200' : 'text-red-200'
                      }`} title="LY Sale vs TY Budget">
                        {(divStats.lySaleVsTyPct || 0) >= 0 ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
                        {(divStats.lySaleVsTyPct || 0) >= 0 ? '+' : ''}{(divStats.lySaleVsTyPct || 0).toFixed(1)}%
                      </div>
                    </>
                  );
                })()}
                {!rightPanelOpen && (
                  <Button variant="ghost" size="sm" className="text-white hover:bg-white/20" onClick={() => setRightPanelOpen(true)}>
                    <Filter className="h-4 w-4 mr-1" />
                    Filters
                  </Button>
                )}
              </div>
            </div>
          )}
          
          {/* Bulk Action Bar */}
          {renderBulkActionBar()}

          {/* Hide Reviewed Toggle + Add Manager + Sync Now (admin only) */}
          {isAdmin && (
            <div className="flex items-center justify-between px-4 pt-2">
              <div className="flex items-center gap-2">
                <button
                  className="inline-flex items-center h-7 px-3 text-xs font-medium rounded-md bg-zinc-800 text-white hover:bg-zinc-700 transition-colors"
                  onClick={() => setShowAddManagerDialog(true)}
                  data-testid="add-manager-btn"
                >
                  <UserPlus className="h-3.5 w-3.5 mr-1.5" />
                  Add Manager
                </button>
                <Select value={syncDivision} onValueChange={setSyncDivision} disabled={syncingToMySQL}>
                  <SelectTrigger className="h-7 w-[150px] text-xs" data-testid="sync-division-select">
                    <SelectValue placeholder="All divisions" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    <SelectItem value="__all__">All divisions</SelectItem>
                    {allDivisions.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  className="inline-flex items-center h-7 px-3 text-xs font-medium rounded-md bg-emerald-700 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50"
                  onClick={async () => {
                    setSyncingToMySQL(true);
                    const _div = (syncDivision && syncDivision !== '__all__') ? syncDivision : null;
                    try {
                      const result = await adminApi.syncToMySQL(_div);
                      const scope = _div ? `${_div} — ` : '';
                      const parts = [];
                      if (result.inserted > 0) parts.push(`${result.inserted} new`);
                      if (result.deleted > 0) parts.push(`${result.deleted} removed`);
                      if (parts.length > 0) {
                        toast.success(`Synced ${scope}${parts.join(', ')}`);
                      } else {
                        toast.info(`${scope}All ${result.updated} lines up to date`);
                      }
                    } catch (err) {
                      toast.error(`Sync failed: ${err.message}`);
                    } finally {
                      setSyncingToMySQL(false);
                    }
                  }}
                  disabled={syncingToMySQL}
                  data-testid="sync-to-mysql-btn"
                >
                  {syncingToMySQL ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <DatabaseZap className="h-3.5 w-3.5 mr-1.5" />}
                  {syncingToMySQL ? 'Syncing...' : (syncDivision && syncDivision !== '__all__' ? `Sync ${syncDivision}` : 'Sync Now')}
                </button>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[10px] text-gray-500 font-medium">Hide Reviewed</label>
                <Switch
                  checked={hideReviewed}
                  onCheckedChange={toggleHideReviewed}
                  className="h-4 w-7"
                  data-testid="toggle-hide-reviewed"
                />
              </div>
            </div>
          )}

          {/* Main content - Account Manager accordions with LAZY LOADING */}
          <div className="flex-1 overflow-y-auto p-4">
            {loadingDivision ? (
              <div className="space-y-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-white border rounded-lg p-4 animate-pulse">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 bg-gray-200 rounded-full" />
                        <div>
                          <div className="h-4 w-32 bg-gray-200 rounded mb-1" />
                          <div className="h-3 w-20 bg-gray-100 rounded" />
                        </div>
                      </div>
                      <div className="flex gap-4">
                        <div className="text-right">
                          <div className="h-2.5 w-10 bg-gray-100 rounded mb-1" />
                          <div className="h-4 w-16 bg-gray-200 rounded" />
                        </div>
                        <div className="text-right">
                          <div className="h-2.5 w-10 bg-gray-100 rounded mb-1" />
                          <div className="h-4 w-16 bg-gray-200 rounded" />
                        </div>
                        <div className="h-5 w-14 bg-gray-100 rounded" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : managerStats.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 py-12 text-center">
                <Search className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                <h3 className="text-base font-medium text-gray-900 mb-1">No managers found</h3>
                <p className="text-sm text-gray-500">Try selecting a different division</p>
              </div>
            ) : filteredManagerStats.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 py-12 text-center">
                <Search className="h-10 w-10 mx-auto text-gray-400 mb-2" />
                <h3 className="text-base font-medium text-gray-900 mb-1">No matching managers</h3>
                <p className="text-sm text-gray-500">Clear filter to see all managers</p>
              </div>
            ) : (
              <Accordion 
                type="single" 
                collapsible 
                className="space-y-2"
                value={expandedManager}
                onValueChange={handleManagerAccordionChange}
              >
                {filteredManagerStats.map((manager) => {
                  const isExpanded = expandedManager === manager.emp_id;
                  const isLoading = loadingManager === manager.emp_id;
                  const lySale = manager.last_year_total || 0;
                  const tyBudget = manager.this_year_total || 0;
                  const tyBudgetAdj = manager.this_year_total_adjusted || tyBudget;
                  const lyBudget = manager.ly_budget_total || 0;
                  const lySaleVsTyPct = manager.ly_sale_vs_ty_pct || 0;
                  const lyBudgetVsTyPct = manager.ly_budget_vs_ty_pct || 0;
                  const hasLyBudget = lyBudget > 0;
                  
                  // Get customers for this manager if expanded (filter by accountManagerId which is emp_id)
                  const managerCustomers = isExpanded 
                    ? filteredCustomers.filter(c => c.accountManagerId === manager.emp_id)
                    : [];

                  // Visible customer count: use filtered count when expanded, backend count otherwise
                  const visibleCount = isExpanded
                    ? filteredCustomers.filter(c => c.accountManagerId === manager.emp_id).length
                    : Math.max(0, (manager.customer_count || 0) - (!showNewDealSuspects ? (manager.suspect_only_count || 0) : 0));
                  
                  // Check reviewed status
                  const reviewKey = `${selectedDivision}|${manager.emp_id}`;
                  const reviewInfo = reviewedManagers[reviewKey];
                  const isReviewedToday = reviewInfo?.is_today;
                  
                  // Hide empty manager cards after filters — but NOT if still loading
                  if (isExpanded && !isLoading && visibleCount === 0 && managerCustomers.length === 0) {
                    // Don't vanish, let filteredManagerStats handle hiding
                    // If we got here, the card passed filteredManagerStats but expanded to 0 customers
                    // (e.g., pipeline filter active). Allow render with empty state.
                  }
                  if (hideReviewed && isReviewedToday) return null;

                  // Use backend pipeline KPIs (accurate from line items) instead of frontend approximation
                  const pipelineKpis = isExpanded && managerPipelineKpis?.pipelines && expandedManager === manager.emp_id
                    ? managerPipelineKpis.pipelines : {};
                  const targetMap = isExpanded && managerPipelineKpis?.target_map && expandedManager === manager.emp_id
                    ? managerPipelineKpis.target_map : {};

                  const hasApprovals = (manager.approved_count || 0) > 0;

                  return (
                    <AccordionItem 
                      key={manager.emp_id} 
                      value={manager.emp_id}
                      className={`border rounded-lg overflow-hidden ${
                        hasApprovals ? 'bg-green-100/80 border-green-300' : 'bg-white border-gray-200'
                      }`}
                    >
                      <AccordionTrigger className={`px-4 py-3 hover:no-underline ${
                        hasApprovals ? 'hover:bg-green-50' : 'hover:bg-gray-50'
                      }`}>
                        <div className="flex items-center justify-between w-full pr-2">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full flex items-center justify-center bg-zinc-100">
                              <User className="h-4 w-4 text-zinc-600" />
                            </div>
                            <div className="text-left">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-gray-900">{manager.name}</p>
                                <span
                                  role="button"
                                  tabIndex={0}
                                  className="h-5 w-5 rounded-full bg-blue-100 hover:bg-blue-200 flex items-center justify-center transition-colors cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenAddDeal(selectedDivision || divisionFilter, manager.emp_id, manager.name);
                                  }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleOpenAddDeal(selectedDivision || divisionFilter, manager.emp_id, manager.name); }}}
                                  title="Add new deal"
                                  data-testid={`add-deal-${manager.emp_id}`}
                                >
                                  <Plus className="h-3 w-3 text-blue-700" />
                                </span>
                                <span
                                  role="button"
                                  tabIndex={0}
                                  className="h-5 w-5 rounded-full bg-emerald-100 hover:bg-emerald-200 flex items-center justify-center transition-colors cursor-pointer"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleExportDataDump(manager.emp_id, selectedDivision);
                                  }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); handleExportDataDump(manager.emp_id, selectedDivision); }}}
                                  title="Export data dump (.xlsx)"
                                  data-testid={`export-${manager.emp_id}`}
                                >
                                  {exportingManager === `${selectedDivision}|${manager.emp_id}` 
                                    ? <Loader2 className="h-3 w-3 text-emerald-700 animate-spin" />
                                    : <Download className="h-3 w-3 text-emerald-700" />}
                                </span>
                                {isReviewedToday && (
                                  <Badge className="text-[8px] h-4 px-1 bg-green-100 text-green-700 border-0">Reviewed</Badge>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">{visibleCount} customers</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {hasLyBudget && (
                              <div className="text-right">
                                <p className="text-[8px] text-gray-400 uppercase">LY Budget</p>
                                <p className="font-mono text-xs text-gray-500">{formatSmartCurrency(lyBudget)}</p>
                              </div>
                            )}
                            <div className="text-right">
                              <p className="text-[8px] text-gray-400 uppercase">LY Sale</p>
                              <p className="font-mono text-xs text-gray-500">{formatSmartCurrency(lySale)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-[8px] text-gray-400 uppercase">TY Budget</p>
                              <p className="font-mono text-xs text-gray-500">{formatSmartCurrency(tyBudget)}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-[8px] text-gray-400 uppercase">TY Act. Bgt</p>
                              <p className="font-mono text-sm font-semibold">{formatSmartCurrency(tyBudgetAdj)}</p>
                            </div>
                            {hasLyBudget && (
                              <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                lyBudgetVsTyPct >= 0 ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'
                              }`} title="LY Budget vs TY Budget">
                                {lyBudgetVsTyPct >= 0 ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
                                {lyBudgetVsTyPct >= 0 ? '+' : ''}{lyBudgetVsTyPct.toFixed(1)}%
                              </div>
                            )}
                            <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                              lySaleVsTyPct >= 0 ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                            }`} title="LY Sale vs TY Budget">
                              {lySaleVsTyPct >= 0 ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
                              {lySaleVsTyPct >= 0 ? '+' : ''}{lySaleVsTyPct.toFixed(1)}%
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="border-t border-gray-100">
                          {/* Pipeline Summary Row with KPIs + Approve/Reviewed buttons */}
                          {!isLoading && !pipelineKpisLoading && Object.keys(pipelineKpis).length > 0 && (
                            <div className="px-4 py-3 bg-gray-50/80 border-b border-gray-100">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Pipeline Summary (from line items)</span>
                                <div className="flex items-center gap-1.5">
                                  <button
                                    className="inline-flex items-center h-6 px-2 text-[10px] font-medium rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors disabled:opacity-50"
                                    onClick={() => handleApproveAll(manager.emp_id, selectedDivision)}
                                    disabled={approvingManager === reviewKey}
                                    data-testid={`approve-all-${manager.emp_id}`}
                                  >
                                    {approvingManager === reviewKey ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                                    Approve All
                                  </button>
                                  {!isReviewedToday && (
                                    <button
                                      className="inline-flex items-center h-6 px-2 text-[10px] font-medium rounded bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors disabled:opacity-50"
                                      onClick={() => handleReviewManager(manager.emp_id, selectedDivision)}
                                      disabled={reviewingManager === reviewKey}
                                      data-testid={`review-${manager.emp_id}`}
                                    >
                                      {reviewingManager === reviewKey ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Eye className="h-3 w-3 mr-1" />}
                                      Reviewed
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="flex gap-2 overflow-x-auto pb-1">
                                {Object.entries(pipelineKpis).filter(([pl]) => pl).map(([pl, kpi]) => {
                                  const lySale = kpi.ly_sale || 0;
                                  const tyBgt = kpi.ty_budget || 0;
                                  const salePct = lySale > 0 ? ((tyBgt / lySale) * 100).toFixed(0) : '-';
                                  const isRepeatDeal = pl === 'REPEAT DEAL';
                                  const existingTarget = targetMap[pl];
                                  const editVal = editingTargets[pl];
                                  const targetBudget = kpi.target_budget || (existingTarget ? tyBgt * existingTarget / 100 : null);
                                  return (
                                    <div key={pl} className="flex-shrink-0 bg-white rounded border border-gray-200 px-2.5 py-1.5 min-w-[160px]" data-testid={`pipeline-kpi-${pl.replace(/\s+/g, '-').toLowerCase()}`}>
                                      <div className="flex items-center justify-between mb-1">
                                        <Badge className={`text-[8px] h-3.5 px-1 border-0 ${
                                          pl === 'NEW DEAL' ? 'bg-blue-100 text-blue-700' :
                                          pl === 'CROSS SELLING' ? 'bg-purple-100 text-purple-700' :
                                          pl === 'UP SELLING' ? 'bg-indigo-100 text-indigo-700' :
                                          pl === 'WIN BACK' ? 'bg-orange-100 text-orange-700' :
                                          isRepeatDeal ? 'bg-emerald-100 text-emerald-700' :
                                          pl === 'INDENTING DEAL' ? 'bg-teal-100 text-teal-700' :
                                          'bg-gray-100 text-gray-600'
                                        }`}>{pl}</Badge>
                                        <span className="text-[8px] text-gray-400">{kpi.line_count || 0} items</span>
                                      </div>
                                      <div className="grid grid-cols-3 gap-1 text-[9px]">
                                        <div><span className="text-gray-400">LY Sale</span><br/><span className="font-medium">{formatSmartCurrency(lySale)}</span></div>
                                        <div><span className="text-gray-400">TY Bgt</span><br/><span className="font-medium">{formatSmartCurrency(tyBgt)}</span></div>
                                        <div><span className="text-gray-400">Sale%</span><br/><span className="font-medium">{salePct}%</span></div>
                                      </div>
                                      {/* Target % input - admin only, not for REPEAT DEAL */}
                                      {!isRepeatDeal && (
                                        <div className="mt-1.5 pt-1.5 border-t border-gray-100">
                                          <div className="flex items-center gap-1">
                                            <span className="text-[8px] text-gray-400 whitespace-nowrap">Target %:</span>
                                            <input
                                              type="number"
                                              min="0"
                                              max="1000"
                                              step="1"
                                              className="w-14 h-5 text-[10px] px-1 border border-gray-200 rounded text-center focus:outline-none focus:ring-1 focus:ring-blue-400"
                                              value={editVal !== undefined ? editVal : (existingTarget || '')}
                                              placeholder="-"
                                              onChange={(e) => setEditingTargets(prev => ({...prev, [pl]: e.target.value}))}
                                              data-testid={`target-pct-${pl.replace(/\s+/g, '-').toLowerCase()}`}
                                            />
                                            <button
                                              className="h-5 px-1.5 text-[9px] bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                              disabled={savingTarget === pl || (editVal === undefined && !existingTarget)}
                                              onClick={() => handleSavePipelineTarget(manager.emp_id, selectedDivision, pl, editVal !== undefined ? editVal : existingTarget)}
                                              data-testid={`save-target-${pl.replace(/\s+/g, '-').toLowerCase()}`}
                                            >
                                              {savingTarget === pl ? '...' : 'Set'}
                                            </button>
                                          </div>
                                          {targetBudget !== null && (
                                            <div className="mt-0.5 text-[9px]">
                                              <span className="text-gray-400">Target Bgt: </span>
                                              <span className="font-semibold text-blue-700">{formatSmartCurrency(targetBudget)}</span>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                          {pipelineKpisLoading && isExpanded && (
                            <div className="px-4 py-3 bg-gray-50/80 border-b border-gray-100">
                              <div className="flex gap-2 animate-pulse">
                                {[1,2,3].map(i => (
                                  <div key={i} className="h-20 w-40 bg-gray-200 rounded" />
                                ))}
                              </div>
                            </div>
                          )}
                          {isLoading ? (
                            <div className="divide-y divide-gray-100">
                              {[...Array(3)].map((_, i) => (
                                <div key={i} className="flex items-center gap-4 px-4 py-3 animate-pulse">
                                  <div className="h-4 w-4 bg-gray-200 rounded" />
                                  <div className="h-4 w-40 bg-gray-200 rounded" />
                                  <div className="h-3 w-20 bg-gray-100 rounded" />
                                  <div className="flex-1" />
                                  <div className="h-4 w-16 bg-gray-200 rounded" />
                                  <div className="h-4 w-16 bg-gray-200 rounded" />
                                  <div className="h-4 w-12 bg-gray-100 rounded" />
                                </div>
                              ))}
                            </div>
                          ) : managerCustomers.length > 0 ? (
                            renderCustomerTable(managerCustomers)
                          ) : (
                            <div className="py-8 text-center">
                              <p className="text-sm text-gray-500">No customers match current filters</p>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            )}
          </div>

          {/* Footer stats */}
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex justify-between">
            <span>
              {accountManagerFilter === 'all' 
                ? `${filteredManagerStats.length} managers • ${filteredManagerStats.reduce((sum, m) => sum + Math.max(0, (m.customer_count || 0) - (!showNewDealSuspects ? (m.suspect_only_count || 0) : 0)), 0)} total customers`
                : `Showing 1 of ${filteredManagerStats.length} managers`}
            </span>
            <span>
              {expandedManager 
                ? `Viewing: ${managerStats.find(m => m.emp_id === expandedManager)?.name || expandedManager}` 
                : 'Click a manager to view customers'}
            </span>
          </div>
        </div>

        {/* Right Panel */}
        {renderRightPanel()}

        {/* Add Manager Dialog */}
        <Dialog open={showAddManagerDialog} onOpenChange={setShowAddManagerDialog}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Add New Account Manager</DialogTitle>
              <DialogDescription>
                Select an employee to add as manager under {selectedDivision || 'the selected division'}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <label className="text-xs font-medium text-gray-600">Select Employee *</label>
                <Select value={addManagerSelectedUser} onValueChange={setAddManagerSelectedUser}>
                  <SelectTrigger data-testid="add-manager-select">
                    <SelectValue placeholder="Choose employee..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {allUsers.filter(u => u.emp_id).map(u => (
                      <SelectItem key={u.emp_id} value={u.emp_id}>
                        {(u.name || '').trim()} <span className="text-gray-400 text-xs ml-1">({u.emp_id})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddManagerDialog(false)}>Cancel</Button>
              <Button 
                onClick={handleAddManager} 
                disabled={!addManagerSelectedUser || addManagerLoading}
                className="bg-zinc-800 hover:bg-zinc-700"
                data-testid="add-manager-submit"
              >
                {addManagerLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
                Add Manager
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Others view - Direct customer list
  const myCustomers = filteredCustomers;
  const myStats = getAccountManagerStats(myCustomers);

  return (
    <div className="h-[calc(100vh-57px)] flex overflow-hidden" data-testid="customer-list-page">
      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Budget Approval</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to approve the budget for <strong>{pendingApproval?.name}</strong>?
              <br /><br />
              This will lock the customer from further editing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmApprove} className="bg-green-600 hover:bg-green-700">
              <ShieldCheck className="h-4 w-4 mr-2" />
              Approve & Lock
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pipeline Drilldown Dialog */}
      {renderDrilldownDialog()}

      {/* Add Deal Dialog */}
      {renderAddDealDialog()}

      {/* Merge Dialog - Using AlertDialog for better compatibility */}
      <AlertDialog open={showMergeDialog} onOpenChange={(open) => {
        setShowMergeDialog(open);
        if (!open) {
          setMergeSearchQuery('');
          setMergeMode('existing');
          setMotherName('');
        }
      }}>
        <AlertDialogContent className="max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Merge Customers</AlertDialogTitle>
            <AlertDialogDescription>
              Merge <strong>{selectedCustomersData.length}</strong> selected customer(s).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            {/* Mode toggle */}
            <div className="flex gap-2 mb-3">
              <Button 
                variant={mergeMode === 'existing' ? 'default' : 'outline'} size="sm"
                onClick={() => setMergeMode('existing')}
              >Merge into Existing</Button>
              <Button 
                variant={mergeMode === 'mother' ? 'default' : 'outline'} size="sm"
                onClick={() => setMergeMode('mother')}
              >Create Mother Customer</Button>
            </div>
            
            {mergeMode === 'mother' ? (
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-gray-600">Mother Customer Name</label>
                  <Input
                    placeholder="e.g. ACME GROUP"
                    value={motherName}
                    onChange={(e) => setMotherName(e.target.value)}
                  />
                  <p className="text-xs text-gray-400 mt-1">This will be used as both the name and code</p>
                </div>
                <div className="bg-blue-50 rounded p-2 text-xs text-blue-700">
                  This will create a new Mother Customer and transfer all budget data from the {selectedCustomersData.length} selected customers into it. Source customers will be hidden from the list.
                </div>
              </div>
            ) : (
              <>
                {loadingMergeTargets ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-gray-400 mr-2" />
                    <span className="text-sm text-gray-500">Loading customers...</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input
                      placeholder="Search customers by name or code..."
                      value={mergeSearchQuery || ''}
                      onChange={(e) => setMergeSearchQuery(e.target.value)}
                      className="w-full"
                    />
                    <div className="max-h-48 overflow-y-auto border rounded-md">
                      {mergeTargets
                        .filter(c => {
                          if (!mergeSearchQuery) return true;
                          const q = mergeSearchQuery.toLowerCase();
                          return (c.name || '').toLowerCase().includes(q) || 
                                 (c.cust_code || '').toLowerCase().includes(q);
                        })
                        .slice(0, 100)
                        .map(c => (
                          <div
                            key={c.cust_code}
                            onClick={() => setMergeTargetCustomer(c.cust_code)}
                            className={`px-3 py-2 cursor-pointer hover:bg-gray-100 border-b last:border-b-0 text-sm ${
                              mergeTargetCustomer === c.cust_code ? 'bg-blue-50 text-blue-700' : ''
                            }`}
                          >
                            <div className="font-medium">{c.name}</div>
                            <div className="text-xs text-gray-500">{c.cust_code}</div>
                          </div>
                        ))}
                      {mergeTargets.filter(c => {
                        if (!mergeSearchQuery) return true;
                        const q = mergeSearchQuery.toLowerCase();
                        return (c.name || '').toLowerCase().includes(q) || 
                               (c.cust_code || '').toLowerCase().includes(q);
                      }).length === 0 && (
                        <div className="px-3 py-4 text-center text-gray-500 text-sm">
                          No customers found
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">
                      {mergeSearchQuery ? 'Showing first 100 matches' : `${mergeTargets.length} customers available - type to search`}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowMergeDialog(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleMergeCustomers} 
              disabled={mergeMode === 'existing' ? (!mergeTargetCustomer || loadingMergeTargets) : !motherName.trim()} 
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Merge className="h-4 w-4 mr-2" />
              {mergeMode === 'mother' ? 'Create Mother' : 'Merge'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transfer Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer Customer to Another Manager</DialogTitle>
            <DialogDescription>
              Transfer {selectedCustomersData.filter(c => !c.approved).length} customer(s) to a different account manager.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={transferManager || undefined} onValueChange={setTransferManager}>
              <SelectTrigger>
                <SelectValue placeholder="Select account manager" />
              </SelectTrigger>
              <SelectContent>
                {allAccountManagers.map(m => (
                  <SelectItem key={m.emp_id} value={m.emp_id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTransferDialog(false)}>Cancel</Button>
            <Button onClick={handleTransferCustomer} disabled={!transferManager} className="bg-blue-600 hover:bg-blue-700">
              <UserPlus className="h-4 w-4 mr-2" />
              Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Division Change Dialog */}
      <Dialog open={showDivisionDialog} onOpenChange={setShowDivisionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Division</DialogTitle>
            <DialogDescription>
              Update division for {selectedCustomersData.filter(c => !c.approved && (c.division === 'NO DIVISION' || c.division === 'OTHERS' || !c.division)).length} customer(s).
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Select value={newDivision} onValueChange={setNewDivision}>
              <SelectTrigger>
                <SelectValue placeholder="Select division" />
              </SelectTrigger>
              <SelectContent>
                {validDivisions.map(d => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDivisionDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateDivision} disabled={!newDivision} className="bg-orange-600 hover:bg-orange-700">
              <FolderEdit className="h-4 w-4 mr-2" />
              Update Division
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Division Dialog */}
      <Dialog open={showAddDivisionDialog} onOpenChange={setShowAddDivisionDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add New Division</DialogTitle>
            <DialogDescription>
              Select a division to add to your panel for leads and budget.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-xs font-medium text-gray-600">Select Division *</label>
            <Select value={addDivisionSelected} onValueChange={setAddDivisionSelected}>
              <SelectTrigger data-testid="add-division-select">
                <SelectValue placeholder="Choose division..." />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                {allDivisions
                  .filter(d => !divisionStats.some(ds => ds.division === d))
                  .map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-gray-400 mt-1">Showing divisions not already in your panel</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDivisionDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAddDivision} 
              disabled={!addDivisionSelected || addDivisionLoading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-division-submit"
            >
              {addDivisionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Division
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Left Panel */}
      {renderLeftPanel()}

      {/* Center Panel - Direct Customer List */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header with user info and budget widget */}
        <div className={`${getDivisionColor(selectedDivision || 'INTERCOM')} px-4 py-2 flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-white" />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-white font-bold">{currentUserName}'s Customers</h1>
                <button
                  className="h-5 w-5 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                  onClick={() => handleOpenAddDeal(selectedDivision || divisionFilter || '', user?.emp_id, currentUserName)}
                  title="Add new deal"
                  data-testid="add-deal-employee"
                >
                  <Plus className="h-3 w-3 text-white" />
                </button>
                <button
                  className="h-5 px-2 text-[10px] font-medium rounded bg-white/20 hover:bg-white/30 text-white transition-colors"
                  onClick={() => setShowAddDivisionDialog(true)}
                  title="Add new division"
                  data-testid="add-division-btn"
                >
                  + Division
                </button>
                <button
                  className="h-5 w-5 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                  onClick={() => selectedDivision && handleExportDataDump(user?.emp_id, selectedDivision)}
                  title="Export data dump (.xlsx)"
                  data-testid="export-employee"
                  disabled={!selectedDivision}
                >
                  {exportingManager === `${selectedDivision}|${user?.emp_id}` 
                    ? <Loader2 className="h-3 w-3 text-white animate-spin" />
                    : <Download className="h-3 w-3 text-white" />}
                </button>
              </div>
              <p className="text-white/70 text-xs">
                {myStats.completed}/{myStats.total} completed
              </p>
            </div>
          </div>
          {/* User Budget Widget - 4 KPIs */}
          {userStats && (
            <div className="flex items-center gap-3" data-testid="user-budget-widget">
              {(userStats.ly_budget_total || 0) > 0 && (
                <div className="text-right">
                  <p className="text-[8px] text-white/50 uppercase">LY Budget</p>
                  <p className="font-mono text-xs text-white/70">{formatSmartCurrency(userStats.ly_budget_total)}</p>
                </div>
              )}
              <div className="text-right">
                <p className="text-[8px] text-white/50 uppercase">LY Sale</p>
                <p className="font-mono text-xs text-white/70">{formatSmartCurrency(userStats.ly_total)}</p>
              </div>
              <div className="text-right">
                <p className="text-[8px] text-white/50 uppercase">TY Budget</p>
                <p className="font-mono text-xs text-white/70">{formatSmartCurrency(userStats.ty_total_raw || userStats.ty_total)}</p>
              </div>
              <div className="text-right">
                <p className="text-[8px] text-white/50 uppercase">TY Act. Bgt</p>
                <p className="font-mono text-sm font-semibold text-white">{formatSmartCurrency(userStats.ty_total)}</p>
              </div>
              {(userStats.ly_budget_total || 0) > 0 && (
                <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                  (userStats.ly_budget_vs_ty_pct || 0) >= 0 ? 'bg-white/20 text-blue-200' : 'bg-white/20 text-orange-200'
                }`} title="LY Budget vs TY Act. Budget">
                  {(userStats.ly_budget_vs_ty_pct || 0) >= 0 ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
                  {(userStats.ly_budget_vs_ty_pct || 0) >= 0 ? '+' : ''}{(userStats.ly_budget_vs_ty_pct || 0).toFixed(1)}%
                </div>
              )}
              <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                userStats.diff >= 0 ? 'bg-white/20 text-green-200' : 'bg-white/20 text-red-200'
              }`} title="LY Sale vs TY Act. Budget">
                {userStats.diff >= 0 ? <TrendingUp className="h-2.5 w-2.5" /> : <TrendingDown className="h-2.5 w-2.5" />}
                {userStats.diff >= 0 ? '+' : ''}{(userStats.diff_pct || 0).toFixed(1)}%
              </div>
            </div>
          )}
        </div>
        
        {/* Pipeline KPI Cards (Employee View) */}
        {!pipelineKpisLoading && managerPipelineKpis?.pipelines && Object.keys(managerPipelineKpis.pipelines).length > 0 && (
          <div className="px-4 py-2 bg-gray-50/80 border-b border-gray-100">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Pipeline Breakdown</span>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {Object.entries(managerPipelineKpis.pipelines).filter(([pl]) => pl).map(([pl, kpi]) => {
                const lySale = kpi.ly_sale || 0;
                const tyBgt = kpi.ty_budget || 0;
                const adjTy = kpi.ty_budget_adjusted || tyBgt;
                const salePct = lySale > 0 ? ((tyBgt / lySale) * 100).toFixed(0) : '-';
                const isRepeatDeal = pl === 'REPEAT DEAL';
                const existingTarget = (managerPipelineKpis.target_map || {})[pl];
                const editVal = editingTargets[pl];
                return (
                  <div key={pl} className="flex-shrink-0 bg-white rounded border border-gray-200 px-2.5 py-1.5 min-w-[160px]" data-testid={`emp-pipeline-kpi-${pl.replace(/\s+/g, '-').toLowerCase()}`}>
                    <div className="flex items-center justify-between mb-1">
                      <Badge className={`text-[8px] h-3.5 px-1 border-0 ${
                        pl === 'NEW DEAL' ? 'bg-blue-100 text-blue-700' :
                        pl === 'CROSS SELLING' ? 'bg-purple-100 text-purple-700' :
                        pl === 'UP SELLING' ? 'bg-indigo-100 text-indigo-700' :
                        pl === 'WIN BACK' ? 'bg-orange-100 text-orange-700' :
                        isRepeatDeal ? 'bg-emerald-100 text-emerald-700' :
                        pl === 'INDENTING DEAL' ? 'bg-teal-100 text-teal-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{pl}</Badge>
                      <span className="text-[8px] text-gray-400">{kpi.line_count || 0} items</span>
                    </div>
                    <div className="grid grid-cols-3 gap-1 text-[9px]">
                      <div><span className="text-gray-400">LY Sale</span><br/><span className="font-medium">{formatSmartCurrency(lySale)}</span></div>
                      <div><span className="text-gray-400">TY Bgt</span><br/><span className="font-medium">{formatSmartCurrency(tyBgt)}</span></div>
                      <div><span className="text-gray-400">Sale%</span><br/><span className="font-medium">{salePct}%</span></div>
                    </div>
                    {!isRepeatDeal && (
                      <div className="mt-1.5 pt-1.5 border-t border-gray-100">
                        <div className="flex items-center gap-1">
                          <span className="text-[8px] text-gray-400 whitespace-nowrap">Target %:</span>
                          <input
                            type="number" min="0" max="1000" step="1"
                            className="w-14 h-5 text-[10px] px-1 border border-gray-200 rounded text-center focus:outline-none focus:ring-1 focus:ring-blue-400"
                            value={editVal !== undefined ? editVal : (existingTarget || '')}
                            placeholder="100"
                            onChange={(e) => setEditingTargets(prev => ({...prev, [pl]: e.target.value}))}
                            data-testid={`emp-target-pct-${pl.replace(/\s+/g, '-').toLowerCase()}`}
                          />
                          <button
                            className="h-5 px-1.5 text-[9px] bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            disabled={savingTarget === pl || (editVal === undefined && !existingTarget)}
                            onClick={async () => {
                              setSavingTarget(pl);
                              try {
                                const pct = parseFloat(editVal !== undefined ? editVal : existingTarget);
                                await approvalApi.setPipelineTarget(user.emp_id, selectedDivision, pl, pct);
                                // Refresh KPIs and stats
                                const [kpisData, statsData] = await Promise.all([
                                  approvalApi.getManagerPipelineKpis(user.emp_id, selectedDivision),
                                  statsApi.getUserStats(selectedDivision)
                                ]);
                                setManagerPipelineKpis(kpisData);
                                setUserStats(statsData);
                                setEditingTargets(prev => { const n = {...prev}; delete n[pl]; return n; });
                                toast.success(`Target ${pct}% saved for ${pl}`);
                              } catch (err) {
                                toast.error(`Failed: ${err.message}`);
                              } finally {
                                setSavingTarget(null);
                              }
                            }}
                            data-testid={`emp-save-target-${pl.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            {savingTarget === pl ? '...' : 'Set'}
                          </button>
                        </div>
                        {existingTarget && (
                          <div className="mt-0.5 text-[9px]">
                            <span className="text-gray-400">Target Bgt: </span>
                            <span className="font-semibold text-blue-700">{formatSmartCurrency(adjTy)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {pipelineKpisLoading && (
          <div className="px-4 py-2 bg-gray-50/80 border-b border-gray-100">
            <div className="flex gap-2 animate-pulse">
              {[1,2,3].map(i => <div key={i} className="h-20 w-40 bg-gray-200 rounded" />)}
            </div>
          </div>
        )}

        {/* Bulk Action Bar */}
        {renderBulkActionBar()}

        {/* Customer Table */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="bg-white border rounded-lg overflow-hidden">
            {loadingCustomers ? (
              <div className="divide-y divide-gray-100">
                {[...Array(10)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4 px-4 py-3 animate-pulse">
                    <div className="h-4 w-4 bg-gray-200 rounded" />
                    <div className="h-4 bg-gray-200 rounded" style={{ width: `${120 + (i % 4) * 30}px` }} />
                    <div className="h-3 w-16 bg-gray-100 rounded" />
                    <div className="h-3 w-20 bg-gray-100 rounded" />
                    <div className="flex-1" />
                    <div className="h-4 w-16 bg-gray-200 rounded" />
                    <div className="h-4 w-16 bg-gray-200 rounded" />
                    <div className="h-4 w-12 bg-gray-100 rounded" />
                  </div>
                ))}
              </div>
            ) : (
              renderCustomerTable(myCustomers)
            )}
          </div>
        </div>

        {/* Footer stats */}
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 flex justify-between">
          <span>Showing {myCustomers.length} customers</span>
          <span>{myCustomers.filter(c => c.status === 'completed' || c.status === 'approved').length} completed</span>
        </div>
      </div>

      {/* Right Panel */}
      {renderRightPanel()}
    </div>
  );
};

export default CustomerList;
